/**
 * Shared domain types — the single source of truth for data shapes across the
 * app. `api/` returns these; components consume them. Derived directly from
 * build-spec §4 (data model), §5 (content/block model), §6 (state machine),
 * and §8 (public read response).
 *
 * When the backend is swapped Xano → FastAPI, only `api/` changes; these types
 * (and therefore every component) stay put.
 */

/* ─── State machine (spec §6) ──────────────────────────────────────────── */

/** The eight quote statuses. Note `converted` — present in the spec's state
 *  machine but missing from the design mockup; added deliberately here. */
export type QuoteStatus =
  | "draft"
  | "sent"
  | "viewed"
  | "accepted"
  | "converted"
  | "declined"
  | "expired"
  | "withdrawn";

export const QUOTE_STATUSES: readonly QuoteStatus[] = [
  "draft",
  "sent",
  "viewed",
  "accepted",
  "converted",
  "declined",
  "expired",
  "withdrawn",
] as const;

/* ─── Enums from the data model (spec §4) ──────────────────────────────── */

export type DrawingType =
  | "planning"
  | "building_regulation"
  | "planning_and_building_regulation"
  | "measured_survey"
  | "feasibility"
  | "other";

export type VatTreatment =
  | "inclusive"
  | "exclusive"
  | "zero_rated"
  | "not_applicable";

export type Currency = "GBP" | string;

export type UserRole = "client" | "admin" | "staff";

/** Convention from spec §4: line-item fee classification. */
export type FeeType = "service" | "disbursement" | "survey";

/* ─── Content / block model (spec §2/§5) ───────────────────────────────── */

/** Six core block types plus five editorial. All eleven are repeatable. */
export type BlockType =
  // core
  | "cover"
  | "rich_text"
  | "image"
  | "pricing"
  | "terms"
  | "accept"
  // editorial
  | "author"
  | "quote"
  | "split"
  | "feature_grid"
  | "embed";

export const CORE_BLOCK_TYPES = [
  "cover",
  "rich_text",
  "image",
  "pricing",
  "terms",
  "accept",
] as const satisfies readonly BlockType[];

export const EDITORIAL_BLOCK_TYPES = [
  "author",
  "quote",
  "split",
  "feature_grid",
  "embed",
] as const satisfies readonly BlockType[];

/** Background modifier — available on ANY block (spec §5A). Not a block type. */
export interface BlockBackground {
  kind: "image" | "color";
  /** when kind="image" — Cloudflare delivery URL */
  asset_url?: string;
  /** when kind="color" — a design token name or hex, e.g. "slate-800" */
  color?: string;
  /** 0–100, darkness of the scrim over an image (default 70) */
  overlay?: number;
}

/** Every block carries an id and an optional background modifier (§5A). */
export interface BlockBase {
  id: string;
  background?: BlockBackground;
}

export interface CoverBlock extends BlockBase {
  type: "cover";
  props: { title: string; subtitle?: string };
}

export interface RichTextBlock extends BlockBase {
  type: "rich_text";
  props: { heading?: string; body_md: string };
}

export interface ImageBlock extends BlockBase {
  type: "image";
  props: { asset_url: string; alt?: string; layout?: "full" | "inset"; caption?: string };
}

export interface PricingBlock extends BlockBase {
  type: "pricing";
  /** Carries NO amounts — renders the quote's line_items rows. */
  props: { heading?: string };
}

export interface TermsBlock extends BlockBase {
  type: "terms";
  props: { heading?: string; body_md: string };
}

export interface AcceptBlock extends BlockBase {
  type: "accept";
  props: { require_name: boolean; ask_decline_reason?: boolean };
}

/* ---- editorial ---- */

export interface AuthorBlock extends BlockBase {
  type: "author";
  props: { name: string; role?: string; note?: string; photo_url?: string };
}

export interface TestimonialBlock extends BlockBase {
  type: "quote";
  props: { text: string; attribution?: string };
}

export interface SplitBlock extends BlockBase {
  type: "split";
  props: {
    heading?: string;
    body_md: string;
    asset_url?: string;
    alt?: string;
    image_side?: "left" | "right";
  };
}

export interface FeatureGridItem {
  icon?: string;
  title: string;
  body?: string;
}

export interface FeatureGridBlock extends BlockBase {
  type: "feature_grid";
  props: { eyebrow?: string; heading?: string; items: FeatureGridItem[] };
}

export interface EmbedBlock extends BlockBase {
  type: "embed";
  props: { url: string; caption?: string };
}

export type Block =
  // core
  | CoverBlock
  | RichTextBlock
  | ImageBlock
  | PricingBlock
  | TermsBlock
  | AcceptBlock
  // editorial
  | AuthorBlock
  | TestimonialBlock
  | SplitBlock
  | FeatureGridBlock
  | EmbedBlock;

/** An unknown/forward-compatible block. Renderers must render these as nothing. */
export interface UnknownBlock extends BlockBase {
  type: string;
  props?: Record<string, unknown>;
}

export type AnyBlock = Block | UnknownBlock;

export interface BlockDocumentJson {
  version: number;
  blocks: AnyBlock[];
}

/** Response from POST quote/image-upload-url (api 203, §6). */
export interface ImageUploadUrlResponse {
  uploadURL: string;
  id: string;
}

/* ─── Line items (spec §4, table 20) ───────────────────────────────────── */

/** Which drawing (if any) the line seeds into the new set when its quote is
 *  provisioned (Accepted Quote Workbench §8.2). "none" = no drawing. Matches the
 *  quote_line_item.drawing_type enum and the drawing.drawing_type vocab (sans
 *  "none"). */
export type QuoteLineDrawingType = "none" | "planning" | "building_control" | "other";

export interface LineItem {
  id: number;
  created_at?: string;
  quote_id: number;
  /** display order, ascending */
  position: number;
  title: string;
  description?: string;
  quantity: number;
  unit_price: number;
  /** STORED, not computed — preserves the price as quoted. */
  line_total: number;
  is_optional: boolean;
  fee_type?: FeeType;
  /** Optional rows only: seeded into the public selection on load (spec §6).
   *  Always present (server defaults false); meaningful when is_optional. */
  default_selected: boolean;
  /** Bulleted "Included" sub-list. null/[] ⇒ renders nothing (spec §5.4). */
  included: string[] | null;
  /** Bulleted "Not included" sub-list. null/[] ⇒ renders nothing. */
  not_included: string[] | null;
  /** Seeds a drawing of this type when the quote is provisioned (§8.2). Defaults
   *  to "none"; the workbench seeds zero drawings until lines are tagged. */
  drawing_type?: QuoteLineDrawingType;
}

/** Body for PATCH quote-line-items/{id} — a JSON-patch envelope carrying ONLY
 *  the fields being changed (gated server-side via object.keys, so sending
 *  `false`/`[]` is honoured rather than dropped). `quantity`/`line_total` are
 *  not accepted: the server forces quantity = 1 (flat-fee) and derives
 *  line_total = unit_price. (Editing only title/description leaves price alone.) */
export interface LineItemUpdate {
  patch: Partial<{
    title: string;
    description: string;
    unit_price: number;
    is_optional: boolean;
    fee_type: string;
    position: number;
    default_selected: boolean;
    included: string[];
    not_included: string[];
    drawing_type: QuoteLineDrawingType;
  }>;
}

/* ─── Quote (spec §4, table 19) ────────────────────────────────────────── */

/** Full quote as seen by staff (table 19). Many fields optional because the
 *  API sends partials on update and some columns are nullable. */
export interface Quote {
  id: number;
  created_at?: string;
  updated_at?: string;
  quote_number: string;
  firebase_quote_id?: string;

  // contact
  contact_first_name?: string;
  contact_last_name?: string;
  company_name?: string;
  is_company?: boolean;
  email?: string;
  phone?: string;

  // site
  site_address_line_1?: string;
  site_address_line_2?: string;
  site_address_line_3?: string;
  site_town?: string;
  site_district?: string;
  site_county?: string;
  site_postcode?: string;
  site_first_line_of_address?: string;
  site_latitude?: number;
  site_longitude?: number;
  /** Unique Property Reference Number — copied from the lead on create-from-lead. */
  uprn?: string;

  // project
  project_description?: string;
  description_long?: string;
  drawing_type?: DrawingType;
  complexity_score?: number;
  bedrooms?: number;
  floors?: number;
  scope_ground_floor?: boolean;
  scope_first_floor?: boolean;
  scope_second_floor?: boolean;
  scope_loft?: boolean;

  // commercials
  /** Server-derived headline value: mandatory items + optional items defaulted
   *  to selected. Recomputed on every line-item change; shown in the quotes
   *  list and pushed to WhatConverts at publish. */
  quote_value?: number;
  /** Authoritative agreed figure snapshotted at accept time = mandatory base +
   *  the optional add-ons the client ticked. Differs from `quote_value` (the
   *  default-selection headline) whenever the client's ticks differ from the
   *  defaults. 0/absent on quotes accepted before this was snapshotted — fall
   *  back to `quote_value`. */
  accepted_total?: number;
  /** ids of the optional line items the client opted into at accept time. */
  selected_optional_line_item_ids?: number[];
  currency?: Currency;
  vat_treatment?: VatTreatment;
  payment_terms?: string;

  // lifecycle
  status: QuoteStatus;
  sent_at?: string;
  first_viewed_at?: string;
  last_viewed_at?: string;
  view_count?: number;
  accepted_at?: string;
  accepted_by_name?: string;
  accepted_ip?: string;
  declined_at?: string;
  decline_reason?: string;
  withdrawn_at?: string;
  expiry_date?: string;
  converted_to_job_at?: string;

  // public page
  public_slug?: string;
  content?: BlockDocumentJson;

  // relations & misc
  client_id?: number;
  job_id?: number;
  internal_notes?: string;

  /** Included by GET quote/{id} (191). */
  line_items?: LineItem[];
}

/* ─── Template (spec §4, table 25) ─────────────────────────────────────── */

export interface QuoteTemplate {
  id: number;
  created_at?: string;
  name: string;
  description?: string;
  content: BlockDocumentJson;
  default_line_items: Array<Partial<LineItem>>;
  is_active: boolean;
  /** At most one template is the default (server-enforced); pre-selected in the
   *  create-quote picker. */
  is_default?: boolean;
}

/* ─── User (spec §4, table 1) ──────────────────────────────────────────── */

export interface User {
  id: number;
  name?: string;
  email: string;
  role: UserRole;
  retool_user_id?: string;
}

/* ─── Public read response (spec §8) ───────────────────────────────────── */

/** The display-only quote shape returned for the `live` state. Internal ids,
 *  UTM, qwilr, and notes are omitted server-side. */
export interface PublicQuote {
  quote_number: string;
  contact_first_name?: string;
  contact_last_name?: string;
  company_name?: string;

  site_address_line_1?: string;
  site_address_line_2?: string;
  site_address_line_3?: string;
  site_town?: string;
  site_county?: string;
  site_postcode?: string;
  site_first_line_of_address?: string;

  project_description?: string;
  description_long?: string;

  content: BlockDocumentJson;

  quote_value?: number;
  currency?: Currency;
  vat_treatment?: VatTreatment;
  payment_terms?: string;
  expiry_date?: string;

  /** display-only line items, the single source of truth for money */
  line_items: LineItem[];
}

/** The `state` discriminator the public read endpoint switches on (spec §8).
 *  `converted` is NOT a public state — the server resolves a converted quote to
 *  `accepted` per build correction #1 (treated the same on the public page). */
export type PublicState =
  | "live"
  | "accepted"
  | "declined"
  | "withdrawn"
  | "expired"
  | "not_found";

/** Discriminated union over `state`. Only `live` carries the full quote. */
export type PublicReadResponse =
  | ({ state: "live" } & PublicQuote)
  | {
      state: "accepted";
      quote_number?: string;
      company_name?: string;
      contact_first_name?: string;
      /** Fills the `{{short_description}}` token in the agreed breakdown. */
      project_description?: string;
      /** Authoritative agreed figure snapshotted at accept time. */
      accepted_total?: number;
      /** Optional items the client opted into (rendered as "included"). */
      selected_optional_line_item_ids?: number[];
      /** Display-only line items for the agreed breakdown. */
      line_items?: LineItem[];
      currency?: Currency;
      vat_treatment?: VatTreatment;
    }
  | { state: "declined"; quote_number?: string }
  | { state: "withdrawn"; quote_number?: string }
  | { state: "expired"; quote_number?: string; expiry_date?: string }
  | { state: "not_found" };

/* ─── Request bodies / responses ───────────────────────────────────────── */

export interface LoginResponse {
  authToken: string;
}

export interface TrackBody {
  is_staff?: boolean;
  session_id?: string;
  user_agent?: string;
}

/** Body for api 333 POST quote/by-slug/{slug}/session — the beacon's per-page-load session.
 *  `session_uid` is a FRESH uuid per page load (a reload is a new session), NOT the per-tab
 *  TrackBody session_id. Idempotent server-side on (quote, session_uid). */
export interface EngagementSessionBody {
  session_uid: string;
  referrer?: string;
  device_type?: string;
  /** Coarse only ("Safari/iOS") — the schema deliberately never stores a full UA string. */
  ua_family?: string;
  is_staff?: boolean;
}

export interface AcceptBody {
  name: string;
  session_id?: string;
  /** ids of the optional line items the client opted into (may be empty). */
  selected_optional_line_item_ids: number[];
}

/** Response from accept. The server validates the optional ids against THIS
 *  quote, recomputes from DB line_totals, and snapshots `accepted_total`
 *  (authoritative — the client-side running total is display-only). */
export interface AcceptResponse {
  ok: boolean;
  state: "accepted";
  accepted_total: number;
  selected_optional_line_item_ids: number[];
}

/** Response from staff accept-on-behalf (api 345) — the public accept's shape
 *  plus `already` (true when the quote was accepted before this call; the
 *  existing acceptance is returned untouched). */
export interface AcceptOnBehalfResponse {
  ok: boolean;
  state: "accepted";
  already: boolean;
  accepted_total: number;
  selected_optional_line_item_ids: number[];
}

export interface DeclineBody {
  reason?: string;
  session_id?: string;
}

export interface QuoteListFilters {
  status?: QuoteStatus;
  site_postcode?: string;
  email?: string;
  client_id?: number;
  job_id?: number;
  open_only?: boolean;
  page?: number;
  per_page?: number;
}

/* ─── Leads (table 27) ─────────────────────────────────────────────────── */

/** Stage ladder. Timestamps elsewhere use epoch ms with 0 = unset.
 *  `consulted` is legacy — its board column duplicated Qualified and was
 *  dropped, but the Xano consultation endpoint (held) still writes it, so it
 *  stays in the union and folds into Qualified for display (effectiveStage). */
export type LeadStage =
  | "new"
  | "to_schedule"
  | "scheduled"
  | "consulted"
  | "qualified"
  | "quoted"
  | "disqualified"
  | "lost";

/** Visible kanban columns in order. */
export const LEAD_PIPELINE_STAGES: readonly LeadStage[] = [
  "new",
  "to_schedule",
  "scheduled",
  "qualified",
  "quoted",
] as const;

/** Off-ramps — kept out of the main board. */
export const LEAD_OFFRAMP_STAGES: readonly LeadStage[] = ["disqualified", "lost"] as const;

export type ConsultationOutcome = "" | "held" | "no_show" | "cancelled" | "not_required";

/* ─── Feasibility (spec §6) ────────────────────────────────────────────── */

/** Property-level facts, asked once for the whole report. Mirrors the Modal
 *  `/generate` body's `property{}` block (feasibility/models.py PropertyInputs).
 *  PD is measured against the *original* dwellinghouse, so the original-house
 *  facts describe the property, not any one extension. */
export interface ProjectBriefProperty {
  dwelling_type?: string; // house | flat
  attachment?: string; // detached | semi_detached | end_terrace | mid_terrace | unknown
  previously_extended?: boolean;
  original_house_age?: string;
  /** yes | no | not_sure — asked as an /analyze follow-up only when a front extension is
   *  proposed; shapes how the engine reports the Class A front-extension exclusion. */
  principal_elevation_fronts_highway?: string;
  /** Asked as an /analyze follow-up only when every Article 4 Direction at the site is
   *  HMO-specific (those remove the C3→C4 change of use, not householder rights); an
   *  explicit `false` tells the engine PD is not removed — the route's Article 4 gate is
   *  lifted and the likelihood-score deduction waived. */
  hmo_use?: boolean;
}

/** One proposed extension line. Mirrors a Modal `extensions[]` entry
 *  (feasibility/models.py ExtensionInput). Enum values are pinned in
 *  @/lib/feasibility (wrong values 422 on /generate). */
export interface ProjectBriefExtension {
  extension_type?: string; // single_storey | two_storey | … (see ENUMS)
  position?: string; // rear | side | front | wrap | multiple
  /** For a wrap-around these describe the REAR LEG, spanning the full width of the
   *  back including the corner — the return leg is the pair below. */
  depth_m?: number;
  width_m?: number;
  /** Wrap-around only, and required by the engine when position is "wrap": an L is two
   *  rectangles and one width/depth pair cannot describe it. The rear leg above owns the
   *  corner, so the return is measured forward from it and the two simply add. */
  return_width_m?: number; // projection beyond the side wall of the house
  return_length_m?: number; // run along the house, measured forward from the rear leg
  /** Mixed-storey only, and required by the engine when extension_type is "mixed_storey":
   *  the upper floor's own footprint, measured from the same wall as the ground floor
   *  (depth_m × width_m). A two-storey rear whose first floor is shallower or narrower than
   *  the ground floor is ONE mixed-storey line with these set — never a single-storey line
   *  plus a first-floor line, which the engine assesses apart (and offers the ground floor
   *  the Larger Home Extension route a two-storey enlargement can't have). */
  upper_depth_m?: number; // projection of the upper floor beyond the existing wall
  upper_width_m?: number; // width of the upper-floor footprint
  height_m?: number;
  eaves_height_m?: number; // send the band's upper edge (2.5 | 3 | 3.5)
  storeys?: number; // auto-derived for single_storey (1) / two_storey + mixed_storey (2)
  distance_to_boundary_m?: number; // usually surfaced via the /analyze follow-ups
  /** Rear extensions only, surfaced via the /analyze follow-ups. True rules out both
   *  permitted development and the Larger Home Extension route (the extension passes a
   *  side elevation), routing the line straight to a full application. */
  wider_than_existing_house?: boolean;
}

/** @deprecated The old flat single-`dimensions` shape (property + one extension's
 *  fields combined). Retained only so {@link normalizeBrief} can migrate briefs
 *  stored before the property/extensions[] split. New code uses
 *  {@link ProjectBriefProperty} + {@link ProjectBriefExtension}. */
export interface ProjectBriefDimensions
  extends ProjectBriefProperty,
    ProjectBriefExtension {}

/** Feasibility inputs captured in the lead drawer (§5.3) and stored on the lead
 *  under `project_brief` (whole-object PATCH — merge before writing). Prefills
 *  the report form; the spine of the Modal `/analyze` + `/generate` bodies. The
 *  report's `description` comes from the lead's `project_description`, not here.
 *  The address parts are populated by the drawer's postcode lookup.
 *
 *  Shape mirrors the engine contract: a `property` block asked once + a repeating
 *  `extensions[]` array (usually one line). `dimensions` is the legacy pre-split
 *  field, read only for migration by `normalizeBrief`. */
export interface ProjectBrief {
  address?: string;
  council?: string; // LPA name (Ideal Postcodes `district`, not `county`)
  uprn?: string;
  latitude?: number;
  longitude?: number;
  budget_band?: string;
  property?: ProjectBriefProperty;
  extensions?: ProjectBriefExtension[];
  /** @deprecated Pre-split storage; migrated by `normalizeBrief`. Never written by new code. */
  dimensions?: ProjectBriefDimensions;
}

/** The site's statutory designation flags (engine `ConstraintFlags`, names verbatim). */
export interface ConstraintFlags {
  conservation_area: boolean;
  listed_building: boolean;
  listed_building_grade: string | null;
  article_4_direction: boolean;
  green_belt: boolean;
  flood_risk_zone: boolean;
  tree_preservation_zone: boolean;
  area_of_outstanding_natural_beauty: boolean;
  scheduled_monument: boolean;
  national_park: boolean;
  world_heritage_site: boolean;
}

/** Structured site constraints from the engine, with provenance. Property-level — one per
 *  report. `lookup_ok: false` means the planning.data.gov.uk lookup didn't complete, so the
 *  all-`false` flags are *unknown*, not "all clear". Persisted onto `lead.constraints`. */
export interface ConstraintsReport {
  flags: ConstraintFlags;
  source: string; // "planning.data.gov.uk"
  checked_at: string | null; // ISO-8601 of the lookup attempt
  lookup_ok: boolean;
}

/** Latest feasibility report metadata (the Modal result minus the inline PDF),
 *  written by POST /lead/{id}/feasibility-report/begin — the report WEB url, not the PDF.
 *  Legacy rows still hold a Xano storage URL from before the web view; `isWebReportUrl`
 *  distinguishes them, because only a web link can be preview-tagged or sent. `null` until a
 *  report exists. */
export interface FeasibilitySummary {
  band?: string; // RAG: e.g. "green" | "amber" | "red"
  score_pct?: number;
  confidence?: string;
  route?: string;
  approval_rate_pct?: number;
  determination_days?: number;
  case_study_count?: number;
  constraint_names?: string[];
  /** Structured constraints — api 213 routes this top-level key onto `lead.constraints`. */
  constraints?: ConstraintsReport;
  generated_at?: number | string;
}

export interface Lead {
  id: number;
  created_at?: number;
  updated_at?: number;

  contact_first_name?: string;
  contact_last_name?: string;
  company_name?: string;
  is_company?: boolean;
  email?: string;
  phone?: string;

  project_description?: string;
  /** Staff-typed short description for the quote — a noun phrase that completes the
   *  quote's "covers…" sentence (becomes `quote.project_description`, the
   *  `{{short_description}}` token, via #211). Not the client's enquiry text. */
  quote_description?: string;
  site_address_line_1?: string;
  site_address_line_2?: string;
  site_address_line_3?: string;
  site_city?: string;
  site_county?: string;
  /** Local planning authority (Ideal Postcodes `district`). */
  site_district?: string;
  site_postcode?: string;
  /** Structured-address extras, captured from the Ideal Postcodes pick on create. */
  uprn?: string;
  lat?: number;
  lng?: number;

  // attribution (read-only in UI)
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  keyword?: string;
  landing_url?: string;
  gclid?: string;
  /** Who sent a direct-contact lead (free text); set via the manual Add-lead form. */
  referred_by?: string;

  consultation_at?: number; // epoch ms; 0 = none booked
  consultation_outcome?: ConsultationOutcome;
  calcom_booking_uid?: string;
  consultation_notes?: string;

  stage: LeadStage;
  disqualified_reason?: string;
  owner?: string;
  next_action_at?: number; // epoch ms; 0 = none

  quote_id?: number; // 0 = none
  client_id?: number;
  job_id?: number;
  /** Public URL of the latest stored feasibility PDF; "" = none yet. */
  feasibility_report_url?: string;
  /** Latest report metadata; null until a report exists. */
  feasibility_summary?: FeasibilitySummary | null;
  /** Structured site constraints from the latest report (api 213 routes them here).
   *  `lookup_ok: false` ⇒ the lookup didn't complete — flags are unknown, regenerate. */
  constraints?: ConstraintsReport | null;
  /** Feasibility inputs captured at consultation; prefills the report form. */
  project_brief?: ProjectBrief | null;
  lost_at?: number;
  archived_at?: number;
  /** Spam marker (epoch ms; 0 = not spam). A flavour of `disqualified`, not a
   *  stage: set via POST /lead/{id}/spam (api 344), which auto-disqualifies;
   *  cleared by any stage move off disqualified. Spam leads are excluded from
   *  the dashboard lead KPIs and marked spam in WhatConverts. */
  spam_at?: number;

  // capture metadata (not shown)
  whatconverts_lead_id?: string;
  whatconverts_profile_id?: string;
  lead_type?: string;
  raw_payload?: unknown;
}

/** Controlled `lead_source` values for the manual Add-lead form (api 214 maps
 *  each to a stored source/medium server-side). */
export type LeadSource =
  | "past_client"
  | "client_referral"
  | "builder_referral"
  | "direct"
  | "other";

/** The stages a manually-added lead may start in (api 214 rejects others). */
export type LeadCreateStage = Extract<LeadStage, "new" | "to_schedule" | "qualified">;

/** Body for POST /lead (api 214) — manual direct-contact lead. The server requires
 *  a name (first OR last) and at least one of email/phone, and always inserts
 *  (duplicates are intentional). */
export interface LeadCreateInput {
  contact_first_name?: string;
  contact_last_name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  project_description?: string;
  /** Optional at create — the drawer's "Description for quote"; see `Lead.quote_description`. */
  quote_description?: string;
  site_address_line_1?: string;
  site_address_line_2?: string;
  site_address_line_3?: string;
  site_city?: string;
  site_county?: string;
  /** Local planning authority (Ideal Postcodes `district`); "" when not used. */
  site_district?: string;
  site_postcode?: string;
  /** Captured from the Find-address (Ideal Postcodes) pick; "" / 0 when not used. */
  uprn?: string;
  lat?: number;
  lng?: number;
  lead_source?: LeadSource;
  referred_by?: string;
  stage?: LeadCreateStage;
}

/** POST /lead response envelope. */
export interface LeadCreateResponse {
  ok: boolean;
  lead: Lead;
}

/** Standard Xano paged envelope for the lead list. */
export interface LeadListResponse {
  itemsTotal: number;
  pageTotal: number;
  curPage: number;
  perPage: number;
  nextPage: number | null;
  prevPage: number | null;
  items: Lead[];
}

/** A lead's linked quote summary (from GET /lead/{id}). */
export interface LeadQuoteLink {
  id: number;
  quote_number: string;
  status: QuoteStatus;
  [k: string]: unknown;
}

/** GET /lead/{id} — full lead plus the linked quote (or null). */
export type LeadDetail = Lead & { quote: LeadQuoteLink | null };

/** PATCH /lead/{id} present-key patch envelope payload (editable keys only).
 *  The full structured site address is writable (#208 widened 2026-08-14, audit §4.3)
 *  so a bad value captured at creation can be corrected instead of propagating
 *  into the quote via #211. */
export interface LeadPatch {
  contact_first_name?: string;
  contact_last_name?: string;
  company_name?: string;
  is_company?: boolean;
  email?: string;
  phone?: string;
  site_address_line_1?: string;
  site_address_line_2?: string;
  site_address_line_3?: string;
  site_city?: string;
  site_county?: string;
  /** Local planning authority (Ideal Postcodes `district`). */
  site_district?: string;
  site_postcode?: string;
  uprn?: string;
  lat?: number;
  lng?: number;
  project_description?: string;
  quote_description?: string;
  consultation_notes?: string;
  owner?: string;
  next_action_at?: number;
  /** Whole-object write — replaces the stored brief (spec §4.3). */
  project_brief?: ProjectBrief;
}

export interface LeadListFilters {
  stage?: LeadStage;
  q?: string;
  include_archived?: boolean;
  page?: number;
  per_page?: number;
}

/* ─── Report engagement (GET /lead/{id}/report-engagement, api 326) ─────────
 * How the client read the feasibility report. Every aggregate here EXCLUDES staff previews
 * (`?preview=1`) and automated fetches (link scanners, unfurls, prefetch) — 326 filters both on
 * the session query and on the dwell join, so what the panel shows is real readers only. */

/** The report's own row: state, the public URL, and the counters. */
export interface ReportEngagementReport {
  token: string;
  version: number;
  status: "pending" | "ready" | "failed" | string;
  cta_variant: "quote" | "general" | string;
  error_message: string;
  generated_at: number;
  /** 0 = the link has not been marked as sent. Nothing writes this yet. */
  sent_at: number;
  /** 0 = never opened. Set once, by the first non-preview, non-automated open. */
  first_viewed_at: number;
  last_viewed_at: number;
  view_count: number;
  download_count: number;
  cta_click_count: number;
  /** Outbound clicks to LPA application records, by the section they sit in. */
  comparable_link_click_count: number;
  case_study_link_click_count: number;
  web_url: string;
}

/** One reading session. Per-session numbers, so these are stamped even when an aggregate isn't. */
export interface ReportViewSession {
  id: number;
  session_uid: string;
  started_at: number;
  last_seen_at: number;
  total_ms: number;
  max_scroll_pct: number;
  downloaded_pdf: boolean;
  cta_variant: string;
  cta_clicked_at: number;
  contact_clicked_at: number;
  referrer: string;
  device_type: "mobile" | "tablet" | "desktop" | string;
  country: string;
  ua_family: string;
  comparable_link_clicks: number;
  case_study_link_clicks: number;
}

/** Dwell per section, already summed and in report order — 326 walks its canonical key list
 *  rather than grouping on the data, so the chart needs no sorting of its own. A key that never
 *  appears is simply absent. */
export interface ReportSectionTotal {
  section_key: string;
  /** "empty" when the section rendered its empty state — a long dwell on "nothing to report
   *  here" is not engagement, so the chart greys it rather than counting it as depth. */
  section_state: "full" | "empty" | string;
  dwell_ms: number;
  sessions_reached: number;
}

export interface ReportEngagement {
  /** null when the lead has no feasibility_report row at all. */
  report: ReportEngagementReport | null;
  sessions: ReportViewSession[];
  section_totals: ReportSectionTotal[];
  /** Sessions BEFORE the preview/automated filter. `total_sessions - sessions.length` is how many
   *  were excluded — which the panel says out loud, because a staff user who has just opened the
   *  report themselves otherwise reads a correct zero as a broken one. Counted from a total rather
   *  than from the two flags: they overlap, so summing separate counts would double-count. */
  total_sessions: number;
}

/* ─── Quote engagement (GET /quote/{id}/engagement, api 335) ────────────────
 * The quote-side mirror of ReportEngagement, fed by the shared beacon on the public quote page
 * (tables quote_view_session / quote_section_dwell). Staff sessions are excluded server-side
 * from the session list and the dwell join; the quote row's own counters (view_count,
 * first/last_viewed_at) remain api 181 track's aggregates and PREDATE per-session tracking, so
 * view_count > 0 with zero sessions means "viewed before tracking shipped (or with JS off)". */

export interface QuoteEngagementQuote {
  id: number;
  quote_number: string;
  status: QuoteStatus | string;
  public_slug: string;
  /** Stamped by publish (api 197). 0 = never published. */
  sent_at: number;
  expiry_date: number;
  first_viewed_at: number;
  last_viewed_at: number;
  view_count: number;
  accepted_at: number;
  declined_at: number;
}

/** One reading session of the public quote page — one row per page load. */
export interface QuoteViewSession {
  id: number;
  session_uid: string;
  started_at: number;
  last_seen_at: number;
  total_ms: number;
  max_scroll_pct: number;
  referrer: string;
  device_type: "mobile" | "tablet" | "desktop" | string;
  ua_family: string;
  is_staff: boolean;
}

export interface QuoteEngagement {
  /** null when the quote id resolves to nothing. */
  quote: QuoteEngagementQuote | null;
  sessions: QuoteViewSession[];
  /** section_key is the renderer BLOCK TYPE (cover, pricing, accept, …), in canonical order —
   *  same shape as the report's totals, so the shared dwell chart renders both. */
  section_totals: ReportSectionTotal[];
  /** Sessions before the staff filter — the "N staff opens excluded" arithmetic. */
  total_sessions: number;
}

/* ─── Jobs hub (Jobs-Hub Brief 01) ─────────────────────────────────────────
 * Powered by the Jobs group (`H6IHDfRP`) GETs /list_v2 + /detail plus a handful of
 * writes, and the Tasks group (`LaJMLlbG`). Shapes are verbatim from the brief's
 * verified contracts — do not infer fields not listed there. */

/** Canonical job status set (brief §2.6). "cancelled" is a TASK status, not a
 *  job status — deliberately absent. */
export type JobStatus =
  | "not_started"
  | "in_progress"
  | "submitted"
  | "with_client"
  | "on_hold"
  | "completed"
  | "archived";

export const JOB_STATUSES: readonly JobStatus[] = [
  "not_started",
  "in_progress",
  "submitted",
  "with_client",
  "on_hold",
  "completed",
  "archived",
] as const;

/** Closed set the legacy `only_open` filter excludes (completed + archived). */
export const JOB_CLOSED_STATUSES: readonly JobStatus[] = ["completed", "archived"] as const;

/** Archived set the `exclude_archived` filter excludes. The jobs list defaults to
 *  hiding only these (completed jobs stay visible); ticking "Include archived"
 *  shows them too. */
export const JOB_ARCHIVED_STATUSES: readonly JobStatus[] = ["archived"] as const;

/** On-hold set the `exclude_on_hold` filter excludes. The jobs list hides these
 *  by default (they live on the Jobs › On Hold page); ticking "Include on hold"
 *  shows them too. */
export const JOB_ON_HOLD_STATUSES: readonly JobStatus[] = ["on_hold"] as const;

/* ─── Drawing-sets model (Addendum 2 — FROZEN) ─────────────────────────────
 * Hierarchy: job → drawing_set (container, the unit of instruction) → drawing
 * (production unit). A job has ONE current set at a time. Every status is
 * DERIVED by the backend recompute routine — the SPA never computes or writes a
 * status; it calls the thin endpoints and re-reads /detail. */

/** Rollup status — per drawing type, and the set/job `header_status`. Used for
 *  the workstream pills. `complete` appears for the `other` type (no
 *  application). `not_applicable` = that type has no drawings in the set. */
export type WorkstreamStatus =
  | "not_applicable"
  | "not_started"
  | "drawings_in_progress"
  | "with_client"
  | "ready_to_submit"
  | "submitted"
  | "approved"
  | "needs_attention"
  | "complete";

/** A single drawing's own lifecycle. */
export type DrawingStatus = "not_started" | "in_progress" | "with_client" | "complete";

/** A drawing's workstream type (also the `seed_types` / `add_drawing` vocab). */
export type WorkstreamType = "planning" | "building_control" | "other";

export const WORKSTREAM_TYPES: readonly WorkstreamType[] = ["planning", "building_control", "other"] as const;

/** A drawing — one production deliverable inside a set. Display code is
 *  `{job_number}-{set_number}-{drawing_number}` (composed client-side). */
export interface Drawing {
  id: number;
  drawing_set_id: number;
  drawing_type: WorkstreamType;
  drawing_number: number;
  /** The editable lifecycle status — also the completeness signal for the set
   *  lock/new-set gate. Drive the editable control from this, NOT the pill. */
  status: DrawingStatus;
  /** Display-only pill value: equals `status` until the drawing is complete,
   *  then surfaces the workstream rollup (ready_to_submit | submitted | approved
   *  | needs_attention) so the card agrees with the job header. The `other` type
   *  stays `complete`. Render the card pill from this via displayStatusToPill. */
  display_status: WorkstreamStatus | DrawingStatus;
  /** The drawing's draw task; 0/null = none yet. Planning/other auto-seed one;
   *  BC gets one only via create_bc_task (so the "Create task" button shows iff
   *  BC && !draw_task_id). */
  draw_task_id: number | null;
  title: string;
}

/** A drawing set — the container/unit of instruction. A returning client next
 *  year = a new set. Display code is `{job_number}-{set_number}`. Per-type
 *  rollups + `header_status` (least-advanced present type) are derived.
 *  `/detail` orders sets current-first, then past sets by `set_number` desc. */
export interface DrawingSet {
  id: number;
  set_number: number;
  label: string;
  planning_status: WorkstreamStatus;
  bc_status: WorkstreamStatus;
  other_status: WorkstreamStatus;
  header_status: WorkstreamStatus;
  is_complete: boolean;
  is_current: boolean;
  closed_at: number; // epoch ms; >0 once demoted by a newer set
  drawings: Drawing[]; // sorted by drawing_number asc
}

/** Body for POST {Jobs}/create_drawing_set. `seed_types` is a comma-separated
 *  subset of planning,building_control,other (the api seam joins the array).
 *  400 "Finish the current drawing set first" if the current set is incomplete. */
export interface DrawingSetCreateInput {
  label?: string;
  seed_types?: WorkstreamType[];
}

/** Body for POST {Jobs}/update_drawing — the edit drawer. ALWAYS send the
 *  current `title` (it's written every call); send `drawing_type` only when
 *  changing it. */
export interface DrawingUpdateInput {
  title?: string;
  drawing_type?: WorkstreamType;
}

/** `{ ok, completed }` from POST {Jobs}/complete_drawing_set — force-finishes
 *  remaining drawings + closes their draw tasks WITHOUT spawning submit tasks. */
export interface CompleteDrawingSetResponse {
  ok: boolean;
  completed: number;
}

/** The server-supported sort fields for GET /list. `last_activity` is the
 *  default (desc): most-recently-active jobs first, including reactivated ones. */
export type JobSortField =
  | "last_activity"
  | "days_remaining"
  | "job_number"
  | "next_deadline"
  | "client_display"
  | "job_name"
  | "stage"
  | "open_work_count"
  | "total_invoiced";

export type JobDeadlineWindow = "all" | "passed" | "7" | "14" | "30";

export interface JobListFilters {
  page?: number;
  per_page?: number;
  sort_field?: JobSortField;
  sort_direction?: "asc" | "desc";
  q?: string;
  /** comma-separated status values */
  stages?: string;
  /** Legacy: excludes both completed and archived. Superseded by `exclude_archived`
   *  for the jobs list; kept for the legacy /list endpoint contract. */
  only_open?: boolean;
  /** Excludes only archived jobs (completed stay visible). Drives the jobs list's
   *  "Include archived" toggle (unticked → exclude_archived: true). */
  exclude_archived?: boolean;
  /** Excludes on-hold jobs. Drives the jobs list's "Include on hold" toggle
   *  (unticked → exclude_on_hold: true); composes with exclude_archived. */
  exclude_on_hold?: boolean;
  deadline_window?: JobDeadlineWindow;
  has_overdue?: boolean;
  has_open_tasks?: boolean;
  /** Only jobs with an unpaid (outstanding) invoice. */
  has_unpaid?: boolean;
  /** Only jobs that have fees but aren't fully invoiced yet. */
  not_fully_invoiced?: boolean;
}

export interface JobsListResponse {
  items: JobListRow[];
  paging: { page: number; per_page: number; total: number; total_pages: number; has_more: boolean };
}

export interface JobListRow {
  id: number;
  job_number: number;
  job_name: string | null;
  client_id: number | null;
  client_display: string; // company name, or "First Last", possibly ""
  site_full_address: string | null;
  postcode: string | null;
  stage: JobStatus; // = job.status (canonical set)
  open_application_count: number;
  open_task_count: number;
  open_work_count: number; // apps + tasks
  has_overdue: boolean;
  next_deadline: string | null;
  deadline_type: string | null; // statutory | appeal | commence_by | lapse | task
  deadline_source: string | null; // application | task
  days_remaining: number | null; // NEGATIVE = overdue, positive = future, null = none
  // Server-maintained recency stamp (Unix ms). The default-sort key. NULL for
  // activity-less shell jobs — render as "—", never feed null to a formatter.
  last_activity_at: number | null;

  // Rollups mirrored from the job's current drawing set (Addendum 2). When all
  // four are "not_applicable" the job has no drawing work → the cell falls back
  // to the legacy `stage` pill. The deprecated *_in_scope booleans are gone.
  planning_status: WorkstreamStatus;
  bc_status: WorkstreamStatus;
  other_status: WorkstreamStatus;
  header_status: WorkstreamStatus; // least-advanced present type
  is_complete: boolean; // derived; no checkbox

  // Billing rollups, aggregated server-side per job. All amounts are GBP decimals.
  // `total_agreed` comes from the fee table (contracted value); invoiced/outstanding
  // come from the INVOICE table — NOT fee.amount_invoiced, which isn't maintained for
  // invoices imported from Xero (a fee can read 0 invoiced while paid invoices exist).
  total_agreed: number; // Σ fee.amount — the job's contracted value (0 = no fees)
  total_invoiced: number; // Σ live invoice.total — "invoiced to date"
  remaining_to_invoice: number; // max(0, total_agreed − total_invoiced)
  total_outstanding: number; // Σ unpaid invoice balances (excl. void/deleted)
  has_billing: boolean; // a fee OR an invoice exists; false → render the cell as "—"
  is_fully_invoiced: boolean; // total_agreed > 0 AND invoiced ≥ agreed (false when no fees)
  has_unpaid_invoice: boolean; // any live invoice with an outstanding balance
  has_overdue_invoice: boolean; // an unpaid invoice past its due_date
}

/* POST {Jobs}/merge — collapses a duplicate "shell" job (loser) into a real
 * numbered job (winner). Repoints the loser's applications/tasks/fees/invoices/
 * quotes/leads to the winner, backfills the winner's empty location fields, then
 * tombstones the loser (merged_into_job_id = winner, status archived). v1 blocks
 * losers that have drawing sets or linked clients. `dry_run` previews, no writes. */
export interface JobMergeRepoint {
  application: number;
  task: number;
  fee: number;
  invoice: number;
  quote: number;
  lead: number;
}
export interface JobMergePreview {
  dry_run: true;
  winner_id: number;
  winner_job_number: number;
  loser_id: number;
  repointed: JobMergeRepoint;
  winner_fields_to_backfill: string[];
  winner_updates_preview: Record<string, unknown>;
}
export interface JobMergeResult {
  dry_run: false;
  winner_id: number;
  winner_job_number: number;
  loser_id: number;
  repointed: JobMergeRepoint;
  winner_backfilled_fields: string[];
  winner: unknown; // full winning job row — not needed for display
}

export interface JobDetailResponse {
  job: {
    id: number;
    job_number: number;
    job_name: string | null;
    full_address: string | null;
    postcode: string | null;
    // Structured site address (backfilled; empty string when not yet enriched).
    // The header builds its display string from these when address_line_1 is set,
    // else falls back to full_address (Job Detail "Get Detailed Address" feature).
    address_line_1: string | null;
    address_line_2: string | null;
    address_line_3: string | null;
    city: string | null;
    county: string | null;
    uprn: string | null;
    status: JobStatus;
    notes: string | null;
    lat: number | null;
    lng: number | null;
    created_at: string;
    next_deadline: string | null;
    deadline_type: string | null;
    deadline_source: string | null;
    deadline_application_reference: string | null;
    deadline_task_title: string | null;
    days_remaining: number | null;
    // Rollups mirrored from the current drawing set (Addendum 2). The deprecated
    // *_in_scope booleans are gone.
    planning_status: WorkstreamStatus;
    bc_status: WorkstreamStatus;
    other_status: WorkstreamStatus;
    header_status: WorkstreamStatus;
    is_complete: boolean;
    /** Site planning constraints (planning.data.gov.uk), bridged from the lead at
     *  quote provision or looked up on demand from the job's UPRN/lat-lng. `{}` until
     *  ever checked; `flags` are gated by `lookup_ok` (false ⇒ unknown, not "all clear"). */
    constraints?: ConstraintsReport | null;
  };
  clients: JobClient[]; // Clients tab (Brief 02) — present now, unused
  /** Builder firms attached to the job (live links only) — the Builders section
   *  on the Clients tab. Absent until the endpoint ships the block. */
  builders?: JobBuilder[];
  applications: JobApplication[];
  /** Nested sets (Addendum 2): current-first, then past sets by set_number desc;
   *  each carries its drawings[]. */
  drawing_sets: DrawingSet[];
  tasks: JobTask[]; // open tasks only
  work_items: WorkItem[];
  letter_summaries: LetterSummary[];
  active_flags: Record<string, true | "unknown">;
  deadlines: DeadlineEntry[];
  counts: {
    clients: number;
    builders?: number;
    applications: number;
    applications_open: number;
    applications_closed: number;
    drawing_sets: number;
    tasks_open: number;
    work_items: number;
    letters: number;
    planning_letters: number;
    bc_letters: number;
  };
}

export interface JobApplication {
  id: number;
  /** The drawing this application is bound to (Addendum 2); null = unbound. */
  drawing_id?: number | null;
  /** The older refused/withdrawn application this one resubmits (consent-thread
   *  link read by stats/consent_totals); 0/null = not a resubmission. */
  supersedes_application_id?: number | null;
  created_at?: number | null;
  app_reference: string | null;
  alternate_reference: string | null;
  previous_reference: string | null;
  type: string | null;
  application_type: string | null;
  is_major: boolean;
  description: string | null;
  site_address: string | null;
  site_postcode: string | null;
  local_authority_or_bcb: string | null;
  status: string | null;
  decision_outcome: string | null;
  fee_amount: number | null;
  fee_status: string | null;
  date_submitted: string | null;
  date_validated: string | null;
  date_decision: string | null;
  date_statutory_deadline: string | null;
  date_appeal_deadline: string | null;
  date_commence_by: string | null;
  date_lapse: string | null;
  latest_letter_type: string | null;
  latest_letter_subtype: string | null;
  latest_letter_date: string | null;
  latest_planning_letter_id: number | null;
  latest_bc_letter_id: number | null;
  flags_summary: Record<string, unknown>;
  /** Council-portal deep link, lazily cached onto the row by the PlanWire lookup
   *  (the working, repaired link — see the PlanWire panel). Persists once set;
   *  surfaced as a "View on council portal" link on the card when present. */
  application_view_url?: string | null;
  closed_at: string | null;
  is_open: boolean;
  next_deadline: string | null;
  deadline_type: string | null;
  days_remaining: number | null;
}

export interface JobTask {
  id: number;
  title: string;
  description: string | null;
  kind: string;
  status: string;
  priority: string;
  due_date: string | null;
  days_remaining: number | null;
  completed_at: string | null;
  assigned_to_user_id: number | null;
  notes: string | null;
  application_id: number | null;
}

export interface WorkItem {
  source_type: "application" | "task";
  source_id: number;
  title: string;
  status: string | null;
  priority: string | null;
  due_date: string | null;
  deadline_type: string | null;
  days_remaining: number | null;
  application_id: number | null;
  application_reference: string | null;
  application_type: string | null;
  task_id: number | null;
  task_kind: string | null;
  assigned_to_user_id: number | null;
  editable: boolean; // true for tasks, false for applications
}

export interface LetterSummary {
  application_id: number;
  application_reference: string | null;
  count: number;
  planning_count: number;
  bc_count: number;
  latest: {
    id: number;
    source: "planning" | "building_control";
    letter_type: string | null;
    letter_subtype: string | null;
    letter_date: string | null;
    confidence: string | null;
  } | null;
}

export interface DeadlineEntry {
  date: string;
  type: string;
  source: "application" | "task";
  application_id: number | null;
  application_reference: string | null;
  task_id: number | null;
  task_title: string | null;
}

/** Present in the detail payload now; consumed by the Clients tab (Brief 02). */
export interface JobClient {
  id: number;
  is_lead: boolean;
  is_company: boolean;
  display: string;
  contact_first_name: string | null;
  contact_last_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  address_line_3: string | null;
  city: string | null;
  county: string | null;
  address: string | null;
  postcode: string | null;
  notes: string | null;
}

/** Body for POST {Jobs}/create. Lead client: EITHER link existing (client_id)
 *  OR create new (individual: first+last; or company: is_company+company_name). */
export interface JobCreateInput {
  job_name?: string;
  full_address?: string;
  postcode?: string;
  status?: JobStatus;
  notes?: string;
  client_id?: number;
  contact_first_name?: string;
  contact_last_name?: string;
  is_company?: boolean;
  company_name?: string;
  email?: string;
}

export interface JobCreateResponse {
  id: number;
  job_number: number;
  job_name: string | null;
  full_address: string | null;
  postcode: string | null;
  status: string;
  client_id: number;
  client_was_created: boolean;
  xero_synced: boolean;
  xero_contact_id: string;
}

export interface JobUpdateStatusResponse {
  id: number;
  status: string;
  previous_status: string;
}

/** Body for PATCH {Jobs}/update_address — writes the structured site address
 *  (+ uprn/lat/lng) onto the job from an Ideal Postcodes selection. A full
 *  overwrite of the address block: every field is written (empty string clears),
 *  so always send the complete set. Re-fetch /detail after. */
export interface JobAddressUpdateInput {
  id: number;
  address_line_1: string;
  address_line_2: string;
  address_line_3: string;
  city: string;
  county: string;
  postcode: string;
  uprn: string;
  lat: number;
  lng: number;
}
export interface JobAddressUpdateResponse {
  job: { id: number; full_address: string | null; postcode: string | null };
}

/** POST {Jobs}/resume_job — un-hold / un-archive. The backend re-derives the
 *  stage from the job's current scope (never guess the resumed stage
 *  client-side), so this returns the job with its freshly-derived `status`.
 *  Re-fetch /list (+ /detail) after. */
export interface JobResumeResponse {
  job: { id: number; status: JobStatus };
}

/** Lean existing-client picker row from GET {Jobs}/clients?q=. */
export interface JobClientPickerItem {
  id: number;
  display_name: string;
  email: string | null;
  is_company: boolean;
}

/* ─── Tasks (Tasks group `LaJMLlbG`) ───────────────────────────────────────
 * status/kind/priority are NOT server-validated enums; the unions below are the
 * dropdown vocab (brief §2.5). Open = anything except done/cancelled. */

export type TaskPriority = "low" | "medium" | "high";
export type TaskStatus = "open" | "in_progress" | "done" | "cancelled";

export const TASK_PRIORITIES: readonly TaskPriority[] = ["low", "medium", "high"] as const;
export const TASK_STATUSES: readonly TaskStatus[] = ["open", "in_progress", "done", "cancelled"] as const;

/** Body for POST {Tasks}/create. `title` required; defaults applied server-side
 *  (kind=billable_work, status=open, priority=medium). */
export interface TaskCreateInput {
  job_id?: number;
  application_id?: number;
  letter_id?: number;
  /** Chat message this task was created from. Create-time only; the server
   *  404s an unknown id and 400s one that belongs to a different job. */
  chat_message_id?: number;
  title: string;
  description?: string;
  kind?: string;
  status?: string;
  priority?: string;
  due_date?: string | null;
  notes?: string;
  assigned_to_user_id?: number;
}

/* ─── Tasks dashboard (cross-job) ──────────────────────────────────────────
 * GET {Tasks}/list without a job_id → every task across all jobs, denormalised.
 * Wider vocab than the per-job drawer: priority adds `urgent`, status adds
 * `blocked`, kind ∈ billable_work|workflow|reminder|internal. Dates are epoch ms;
 * days_remaining is server-computed (UTC-midnight snapped). */

export type TaskSortField = "due_date" | "priority" | "created_at" | "title" | "status";
export type TaskDueWindow = "all" | "overdue" | "today" | "7" | "14" | "30" | "no_due_date";

export interface TaskListFilters {
  page?: number;
  per_page?: number;
  sort_field?: TaskSortField;
  sort_direction?: "asc" | "desc";
  job_id?: number;
  application_id?: number;
  assigned_to_user_id?: number;
  statuses?: string;
  kinds?: string;
  priorities?: string;
  due_window?: TaskDueWindow;
  q?: string;
  include_done?: boolean;
  include_cancelled?: boolean;
}

/** A /list row — denormalised, every field present. */
export interface TaskRow {
  id: number;
  created_at: number;
  title: string;
  description: string | null;
  kind: string;
  status: string;
  priority: string;
  due_date: number | null;
  days_remaining: number | null; // <0 overdue · 0 today · >0 future · null none
  completed_at: number | null;
  notes: string | null;
  assigned_to_user_id: number | null;
  job_id: number | null;
  job_number: number | null;
  job_name: string | null;
  job_postcode: string | null;
  application_id: number | null;
  application_reference: string | null;
  application_type: string | null;
  application_status: string | null;
}

export interface TaskListResponse {
  items: TaskRow[];
  paging: { page: number; per_page: number; total: number; total_pages: number; has_more: boolean };
}

/* ─── Tasks Kanban board ───────────────────────────────────────────────────
 * GET {Tasks}/board + PATCH {Tasks}/move + PATCH {Tasks}/reorder. The board's
 * six lanes; `completed` is capped to recent completions server-side
 * (completed_within_days, default 14).
 * Verified against the live /board payload on 2026-06-18. */

export type BoardStage =
  | "in_progress"
  | "up_next"
  | "later"
  | "with_client"
  | "completed"
  | "not_scheduled";

/** Task provenance (Addendum 1). `draw`/`submit` cards are auto-generated by the
 *  backend recompute; `manual` cards are user tasks (unchanged behaviour). Drives
 *  the board's drag guards: submit = non-draggable; an unbound draw opener can't
 *  go to with_client/completed. */
export type TaskType = "manual" | "draw" | "submit";

/** A /board row. Same denormalised shape as {@link TaskRow} plus `board_stage`.
 *  NB: the live join returns job_number/job_name/job_postcode (and the
 *  application_* fields) as either a scalar OR a single-element array depending
 *  on the row — `getTaskBoard` normalises them to scalars before they reach the
 *  UI, so callers can treat them like {@link TaskRow}. */
export interface TaskBoardRow extends TaskRow {
  board_stage: BoardStage;
  /** Manual within-lane order (1-based, written by PATCH /reorder). 0 = never
   *  hand-placed — the board shows those after positioned cards, in the
   *  computed priority/due/created fallback order. */
  board_position: number;
  /** Addendum 2 — `submit` cards are auto-managed (non-draggable; /move 400s
   *  them). `getTaskBoard` defaults an absent value to "manual". */
  task_type: TaskType;
  /** Parent job's status, denormalised by /board (board-only — /list rows don't
   *  carry it). The board hides tasks whose job is `on_hold`. Null when the task
   *  has no job; like the other job_* fields it can arrive as a scalar-or-array,
   *  so `getTaskBoard` normalises it. */
  job_status: JobStatus | null;
}

export interface TaskBoardResponse {
  items: TaskBoardRow[];
}

export interface TaskBoardFilters {
  assigned_to_user_id?: number;
  job_id?: number;
  application_id?: number;
  completed_within_days?: number;
}

/** Body for PATCH {Tasks}/move — the id travels in the body, not the path. The
 *  backend owns all status/completed_at side-effects; send only these two. */
export interface TaskMoveInput {
  id: number;
  board_stage: BoardStage;
}

/** Body for PATCH {Tasks}/reorder — ONE lane's task ids in display order; the
 *  backend stamps a 1-based board_position onto each. Always send the lane's
 *  FULL order (the board's Mine filter is client-side, so the client has every
 *  card), which keeps a lane's positions dense and unique. */
export interface TaskReorderInput {
  ordered_ids: number[];
}

/** Body for PATCH {Tasks}/update — all optional except id. `deleted:true` soft-
 *  deletes; `status:"done"` auto-fills completed_at server-side. */
export interface TaskUpdateInput {
  id: number;
  title?: string;
  description?: string;
  kind?: string;
  status?: string;
  priority?: string;
  notes?: string;
  application_id?: number;
  due_date?: string | null;
  assigned_to_user_id?: number;
  /** Clear the assignment (server writes 0). A dedicated flag because Xano's
   *  input layer swallows an explicitly-sent 0 on an optional int input —
   *  `assigned_to_user_id: 0` is silently ignored (verified live 2026-08-26).
   *  Wins over assigned_to_user_id when both are sent. */
  unassign?: boolean;
  deleted?: boolean;
}

/** GET {Tasks}/staff-users — assignable task owners (console roles: admin +
 *  staff; the same population the staff MCP server resolves assignees
 *  against). id + name + avatar_url, name-sorted. */
export interface StaffUser {
  id: number;
  name: string;
  /** Public vault URL of the user's avatar image (t1 `avatar`, uploaded via
   *  the Xano admin); null when unset — render initials instead. */
  avatar_url: string | null;
}

/* ─── Jobs hub — Clients tab (Brief 02) ────────────────────────────────────
 * The tab renders from the detail payload's clients[] + counts.clients (already
 * defined above as JobClient). These are the write-side contracts (Jobs group),
 * verified against live on 2026-06-18. After ANY write, re-fetch /detail. The
 * single-lead invariant is server-owned — never set/clear leads client-side. */

/** GET {Jobs}/client_search — richer, stub-inclusive add-client picker. `q`
 *  under 2 chars returns empty (no error). */
export interface ClientSearchResult {
  id: number;
  display: string;
  is_company: boolean;
  contact_first_name: string | null;
  contact_last_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  postcode: string | null;
  xero_contact_id: string | null; // present but unused in Brief 02
}

export interface ClientSearchResponse {
  results: ClientSearchResult[];
  count: number;
}

/** POST {Jobs}/client_attach — link an existing client (idempotent). Note the
 *  field is `make_lead`, NOT `is_lead`. */
export interface ClientAttachInput {
  job_id: number;
  client_id: number;
  make_lead?: boolean;
}
export interface ClientAttachResponse {
  job_id: number;
  client_id: number;
  already_linked: boolean;
  is_lead: boolean;
}

/** POST {Jobs}/client_create_and_attach — create a client + link it. Identity
 *  required: individual (first+last) OR company (is_company + company_name).
 *  Composes `address` server-side from the split fields. NO Xero push. */
export interface ClientCreateAttachInput {
  job_id: number;
  is_company?: boolean;
  company_name?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  email?: string;
  phone?: string;
  address_line_1?: string;
  address_line_2?: string;
  address_line_3?: string;
  city?: string;
  county?: string;
  postcode?: string;
  notes?: string;
  make_lead?: boolean;
}
export interface ClientCreateAttachResponse {
  job_id: number;
  client_id: number;
  is_lead: boolean;
  client: unknown; // full new record — unused (we re-fetch /detail)
}

/** POST {Jobs}/client_set_lead — atomic clear-all-then-set. */
export interface ClientSetLeadResponse {
  job_id: number;
  lead_client_id: number;
}

/** PATCH {Jobs}/client_update — partial update; only sent fields change. Send
 *  `id` as a number and `is_company` as a boolean (never quoted), and the SPLIT
 *  address fields (NOT the composed `address`). Omit `sync_to_xero`. */
export interface ClientUpdateInput {
  id: number;
  is_company?: boolean;
  company_name?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  email?: string;
  phone?: string;
  address_line_1?: string;
  address_line_2?: string;
  address_line_3?: string;
  city?: string;
  county?: string;
  postcode?: string;
  notes?: string;
}
export interface ClientUpdateResponse {
  client: unknown;
  xero_synced: boolean; // informational only — never block
  xero_sync_error: string;
}

/** POST {Jobs}/client_detach — removes the job_client LINK only (never the
 *  record). Idempotent; never blocks the last client. Lead handling: auto-
 *  promotes when exactly one other remains, else leaves lead_vacant. */
export interface ClientDetachResponse {
  detached: boolean;
  already_detached: boolean;
  was_lead: boolean;
  new_lead_client_id: number | null;
  lead_vacant: boolean;
  clients_remaining: number;
  job_id: number;
  client_id: number;
}

/* ─── Clients section (Jobs group `H6IHDfRP` — clients/list, clients/detail,
 * clients/create) ──────────────────────────────────────────────────────────
 * A first-class directory of every client with per-client activity counts,
 * drilling through to a client hub. Complements the per-job client management
 * on jobDetail (which uses the client_* writes above). The list returns ALL
 * rows in one call; React does the search/filter/sort/pagination. Edits reuse
 * the existing client_update (ClientUpdateInput above). */

/** One row of GET {Jobs}/clients/list. `status` is the raw enum ("active" |
 *  "archived" | "" for legacy rows); `is_archived` is the derived convenience.
 *  is_incomplete = email empty OR xero_contact_id empty. */
export type ClientPortalState = "none" | "pending" | "active" | "disabled";

export interface ClientListRow {
  id: number;
  display_name: string;
  is_company: boolean;
  email: string;
  phone: string;
  postcode: string;
  job_count: number;
  open_task_count: number;
  open_application_count: number;
  is_incomplete: boolean;
  xero_synced: boolean;
  is_archived: boolean;
  status: string;
  created_at: number;
  /** App (portal) access — four states, not a yes/no, so an invite nobody has
   *  acted on ("pending") never reads the same as a client who was never
   *  invited ("none"). Decided by the oldest non-review portal_user row,
   *  exactly as clients/detail decides its `portal` block. */
  portal_state: ClientPortalState;
  /** Epoch ms of the last completed app sign-in (stamped only by the portal's
   *  auth/verify, so "last signed in", not "last active"); 0 when never. */
  portal_last_login_at: number;
}

export interface ClientsListResponse {
  items: ClientListRow[];
  total: number;
}

/** Full client row as returned in GET {Jobs}/clients/detail `client` + the
 *  POST {Jobs}/clients/create `client`. All columns; empty strings (not null)
 *  for unset text fields, per the Xano defaults. */
export interface ClientRecord {
  id: number;
  created_at: number;
  is_company: boolean;
  company_name: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_role: string;
  email: string;
  phone: string;
  address: string;
  address_line_1: string;
  address_line_2: string;
  address_line_3: string;
  city: string;
  county: string;
  postcode: string;
  notes: string;
  xero_contact_id: string;
  status: string;
  xero_status: string | null;
}

export interface ClientJobRow {
  id: number;
  job_number: number;
  job_name: string;
  address: string;
  postcode: string;
  status: string;
  is_lead: boolean;
}

export interface ClientTaskRow {
  id: number;
  title: string;
  job_id: number;
  due_date: number | string | null;
  status: string;
  priority: string;
}

export interface ClientAppRow {
  id: number;
  reference: string;
  job_id: number;
  status: string;
  type: string;
  application_type: string;
  site_address: string;
  local_authority_or_bcb: string;
}

/** Invoice rollups aggregated across the client's jobs (voided/deleted
 *  excluded). outstanding & overdue read invoice.amount_due (Xero net of
 *  credits), matching the per-job invoices tab. */
export interface ClientFinancials {
  total_invoiced: number;
  outstanding: number;
  overdue: number;
}

/** The client's access to the mobile app, from {Jobs}/clients/detail. The
 *  portal_user row IS the invitation — there is no self-signup — so `invited`
 *  is simply "a row exists". `has_signed_in` distinguishes an invite that has
 *  been acted on from one still sitting unopened in an inbox. App Store review
 *  accounts are excluded server-side and never read as invited. */
export interface ClientPortalAccess {
  invited: boolean;
  portal_user_id: number;
  email: string;
  is_active: boolean;
  has_signed_in: boolean;
  /** Epoch ms; 0 when never signed in. */
  last_login_at: number;
  /** Epoch ms the portal_user row was created, i.e. when they were invited. */
  invited_at: number;
  /** >1 when the client has several named contacts with app access. */
  user_count: number;
}

export interface ClientDetailResponse {
  client: ClientRecord;
  jobs: ClientJobRow[];
  open_tasks: ClientTaskRow[];
  open_applications: ClientAppRow[];
  financials: ClientFinancials;
  xero_synced: boolean;
  portal: ClientPortalAccess;
}

/** Response from POST {Jobs}/portal/invite. `created` false = the client already
 *  had access and this was a resend. The email is best-effort and never fails
 *  the call: `email_sent` false with `email_reason` "not_configured" means the
 *  invite template id is missing from sendgrid_credentials — access was still
 *  granted, nothing was emailed. */
export interface PortalInviteResponse {
  portal_user_id: number;
  email: string;
  name: string;
  client_id: number;
  is_active: boolean;
  last_login_at: number;
  created: boolean;
  email_sent: boolean;
  email_reason: string;
}

/** Body for POST {Jobs}/clients/create — a standalone client (no job link).
 *  Identity required: individual (first+last) OR company (is_company +
 *  company_name). The server composes `address` from the split fields and does
 *  NOT push to Xero (lazy at invoice time), so the response xero_synced is
 *  always false on create. */
export interface ClientCreateInput {
  is_company?: boolean;
  company_name?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  contact_role?: string;
  email?: string;
  phone?: string;
  address_line_1?: string;
  address_line_2?: string;
  address_line_3?: string;
  city?: string;
  county?: string;
  postcode?: string;
  notes?: string;
}

export interface ClientCreateResponse {
  client: ClientRecord;
  xero_synced: boolean;
}

/** POST {Jobs}/clients/archive — sets status=archived AND archives the Xero
 *  contact (best-effort). 400 unless the client is eligible (every job archived,
 *  no open tasks/applications, nothing outstanding/overdue). xero_archived is
 *  false with an xero_error ("no_xero_contact_id", "xero_request_failed", …)
 *  when the Xero side couldn't be archived — the local archive still succeeded. */
export interface ClientArchiveResponse {
  client: ClientRecord;
  xero_archived: boolean;
  xero_error: string;
}

/** POST {Jobs}/clients/reopen — sets status=active for an archived client. Does
 *  NOT touch Xero (the opposite of archive): the Xero contact, if any, must be
 *  reactivated manually, so `has_xero_contact` flags whether to remind the user. */
export interface ClientReopenResponse {
  client: ClientRecord;
  has_xero_contact: boolean;
}

/** A row of GET {Jobs}/clients/archive_candidates — a non-archived client whose
 *  engagement is wound down (≥1 job, none in active work). `ready` is the server
 *  archive predicate (every job archived, no open tasks/applications, nothing
 *  outstanding/overdue); when false the count fields explain why (completed but
 *  not-yet-archived jobs, open work, or an outstanding balance). */
export interface ClientArchiveCandidate {
  id: number;
  display_name: string;
  is_company: boolean;
  email: string;
  job_count: number;
  archived_job_count: number;
  completed_job_count: number;
  open_task_count: number;
  open_application_count: number;
  outstanding: number;
  overdue: number;
  ready: boolean;
}

export interface ClientArchiveCandidatesResponse {
  items: ClientArchiveCandidate[];
  total: number;
}

/* ─── Jobs hub — Application lifecycle (Brief 03) ──────────────────────────
 * Makes the Applications tab actionable: create, set-status, close/reopen.
 * Applications group `CI935tE1`. Only `status` (set_status) and `closed_at`
 * (close) are mutable through the API — there is NO general update endpoint, so
 * reference/address/description/subtype/decision/dates/fee are create-time only.
 * After any write, re-fetch the Jobs /detail. */

/** The planning/BC switch on an application (`JobApplication.type`). */
export type ApplicationKind = "planning" | "building_control";

/** Server-validated set_status vocabulary, grouped by kind for the UI. `draft`
 *  (create-time only) and `closed` (use the close action) are deliberately
 *  excluded from both lists. */
export const PLANNING_STATUS_OPTIONS: readonly string[] = [
  "pending_validation",
  "validated",
  "submitted",
  "approved",
  "approved_with_conditions",
  "refused",
  "withdrawn",
  "appeal_approved",
  "appeal_dismissed",
  "on_hold",
] as const;

export const BC_STATUS_OPTIONS: readonly string[] = [
  // "submitted" is required so completing a BC drawing's submit task has a
  // matching status to set (Addendum 2 §4.7); status is free-text server-side.
  "submitted",
  "plans_pending",
  "plans_approved",
  "plans_rejected",
  "inspections_ongoing",
  "completion_certificate_issued",
  "withdrawn",
  "on_hold",
] as const;

/** Free-text subtype suggestions (`application_type`) — NOT server-validated. */
export const PLANNING_SUBTYPES: readonly string[] = [
  "householder",
  "full",
  "clopud",
  "nma",
  "pre_app",
  "listed_building",
  "advertisement",
  "discharge_of_condition",
  "prior_approval",
  "other",
] as const;

export const BC_SUBTYPES: readonly string[] = [
  "full_plans",
  "building_notice",
  "regularisation",
  "reversion",
  "other",
] as const;

/** GET {Apps}/authority/list — LPA/BCB picker. Render + submit `canonical_name`. */
export interface AuthorityRow {
  id: number;
  canonical_name: string;
  kind: string;
  is_active: boolean;
}
export interface AuthorityListResponse {
  count: number;
  items: AuthorityRow[];
}

/** POST {Apps}/create — create an application under a job. Blank `app_reference`
 *  is allowed (creates a stub the letter matcher later adopts); the duplicate
 *  guard only fires for a non-blank ref + same authority. */
export interface ApplicationCreateInput {
  job_id: number;
  type: ApplicationKind;
  local_authority_or_bcb: string;
  site_address: string;
  site_postcode: string;
  app_reference?: string;
  application_type?: string;
  description?: string;
  is_major?: boolean;
  /** Addendum 2 — bind to a drawing so submitting this application closes that
   *  drawing's submit task. Omit / 0 = unbound. The backend validates the
   *  drawing belongs to the job and its type matches `type`. */
  drawing_id?: number;
}
export interface ApplicationCreateResponse {
  application: JobApplication;
}

/** PATCH {Apps}/application/set_status — patches `status` only (never
 *  closed_at). Invalid value → inputerror. */
export interface ApplicationSetStatusResponse {
  application: JobApplication;
}

/** PATCH {Apps}/close — close (closed_at = now) or reopen (`unclose:true`).
 *  Returns the still-open linked tasks so the UI can offer to cancel them. */
export interface ApplicationCloseResponse {
  application: JobApplication;
  linked_open_tasks: Array<{
    id: number;
    title: string;
    status: string;
    priority: string;
    due_date: string | null;
  }>;
  linked_open_tasks_count: number;
}

/* ─── PlanWire planning-status lookup (group `42dmnPbY`) ────────────────────
 * GET {PlanWire}/application-status?application_id — a READ-ONLY, on-demand pull
 * of the live planning status PlanWire holds for an application. Admin-gated
 * (auth = user). The endpoint reads the application row server-side, derives the
 * council + reference, and makes the PlanWire call (a billed third-party request,
 * so trigger it on an explicit user action — never on mount/poll). It deliberately
 * does NOT map onto the internal `application.status` enum: the strings here are
 * raw scraped portal wording (per-council vocab) and are informational only — the
 * user reads them and edits the internal status by hand. Three response shapes,
 * discriminated on `covered` then `found`. */

export interface PlanWireDates {
  applicationDate: string | null;
  applicationDateSource?: string | null;
  receivedDate: string | null;
  validatedDate: string | null;
  decisionDate: string | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
}

export type PlanWireStatusResponse =
  // No PlanWire council mapping for this authority — neutral, not an error.
  | {
      covered: false;
      reason: string;
      authority_id: number;
      authority_name: string;
      app_reference: string;
      cached_view_url: string;
    }
  // Covered, but PlanWire returned 404 for the reference — neutral, not an error.
  | {
      covered: true;
      found: false;
      authority_id: number;
      authority_name: string;
      planwire_council_id: string;
      app_reference: string;
      cached_view_url: string;
      reason: string;
    }
  // Found — the useful case.
  | {
      covered: true;
      found: true;
      authority_id: number;
      authority_name: string;
      planwire_council_id: string;
      app_reference: string;
      planwire_reference: string | null;
      /** Raw scraped portal string — NOT normalised; render verbatim. */
      status: string | null;
      /** Raw scraped decision text; often null until decided. The headline. */
      decision: string | null;
      application_type: string | null;
      dates: PlanWireDates;
      /** Raw PlanWire link — may be a dead 403 skin; do NOT link this. */
      source_url: string | null;
      /** The link to actually use (server-repaired). May be empty/null. */
      view_url: string | null;
      /** What was already cached on the row before this call (server bookkeeping). */
      cached_view_url: string;
      /** True if this call just cached the repaired portal link (server bookkeeping). */
      saved_view_url: boolean;
      /** Full unwrapped PlanWire record — best-effort; treat every field as optional. */
      raw: Record<string, unknown>;
    };

/* ─── Open applications (Applications group `CI935tE1`) ─────────────────────
 * GET {Apps}/open_applications — the cross-job feed behind Jobs › Open
 * applications. Every still-open application (closed_at == 0, merge soft-deletes
 * excluded), bucketed by `type` into planning + building_control, each enriched
 * with a compact parent-job object. Read-only; letters load lazily per row via
 * {@link LetterListFilters}.application_id (the same opening-link the job-detail
 * card uses). Dates here are ISO strings (mirrors {@link JobApplication}). */

/** The compact parent job carried on each open-application row. */
export interface OpenApplicationJob {
  id: number;
  job_number: number;
  job_name: string | null;
  full_address: string | null;
  postcode: string | null;
  status: string;
}

/** One open-application row. A lean projection of {@link JobApplication} plus the
 *  parent `job` and the server-computed `days_to_statutory` (negative = overdue;
 *  null = no statutory deadline). */
export interface OpenApplicationRow {
  id: number;
  job_id: number;
  job: OpenApplicationJob | null;
  type: string | null; // "planning" | "building_control"
  app_reference: string | null;
  alternate_reference: string | null;
  previous_reference: string | null;
  application_type: string | null;
  is_major: boolean;
  description: string | null;
  site_address: string | null;
  site_postcode: string | null;
  local_authority_or_bcb: string | null;
  status: string | null;
  decision_outcome: string | null;
  fee_amount: number | null;
  fee_status: string | null;
  date_submitted: string | null;
  date_validated: string | null;
  date_decision: string | null;
  date_statutory_deadline: string | null;
  days_to_statutory: number | null;
  latest_letter_type: string | null;
  latest_letter_subtype: string | null;
  latest_letter_date: string | null;
  latest_planning_letter_id: number | null;
  latest_bc_letter_id: number | null;
  /** Live letter counts (across the planning + BC letter tables). `letter_count`
   *  gates the inline letters opening-link — when 0, the link is hidden. */
  letter_count: number;
  planning_letter_count: number;
  bc_letter_count: number;
  flags_summary: Record<string, unknown>;
  /** Council-portal deep link (the repaired link cached by a PlanWire lookup).
   *  Surfaced as a "View on council portal" link on the card when present —
   *  mirrors the job-detail application card. */
  application_view_url?: string | null;
  match_status: string | null;
  created_at: string;
}

export interface OpenApplicationsResponse {
  planning: OpenApplicationRow[];
  building_control: OpenApplicationRow[];
  counts: { planning: number; building_control: number; total: number };
}

/* ─── Review & Merge — Phase 1 (Applications group `CI935tE1`) ──────────────
 * The reconciliation triage feed. `GET application/review-queue` surfaces apps
 * the letter pipeline couldn't confidently place: `unmatched_applications`
 * (open, `job_id = 0`, needs_review — each with suggested jobs/sibling apps) and
 * `duplicate_applications` (groups sharing a normalised reference + authority).
 * Read-only except `link_to_job`; record merge/dismiss are Phase 1b (not built).
 * NB: date fields here are Unix ms (0 = none), unlike JobApplication's ISO strings. */

export type ReviewMatchConfidence = "address_match" | "postcode_only";

/** One application row as it appears in the review queue (a lean summary, not the
 *  full {@link JobApplication}). */
export interface ReviewAppSummary {
  id: number;
  app_reference: string | null;
  previous_reference: string | null;
  type: string | null; // "planning" | "building_control"
  status: string | null; // lifecycle status
  match_status: string | null; // needs_review | needs_review_ref_collision | auto_created | matched
  local_authority_or_bcb: string | null;
  authority_id: number | null;
  site_postcode: string | null;
  site_address: string | null;
  job_id: number; // 0 = unlinked
  latest_letter_type: string | null;
  latest_letter_subtype: string | null;
  latest_letter_date: number; // Unix ms, 0 = none
  date_statutory_deadline: number; // Unix ms, 0 = none
}

/** A job the server thinks an unmatched app might belong to, with how it matched. */
export interface ReviewSuggestedJob {
  id: number;
  job_number: number;
  job_name: string | null;
  full_address: string | null;
  postcode: string | null;
  status: string | null;
  confidence: ReviewMatchConfidence;
}

export interface ReviewUnmatchedApp extends ReviewAppSummary {
  suggested_jobs: ReviewSuggestedJob[];
  /** Sibling apps sharing the normalised ref + authority; some may already sit on
   *  a job (`job_id > 0`), giving a "link to the same job" shortcut. */
  suggested_apps: ReviewAppSummary[];
}

export interface ReviewDuplicateGroup {
  /** For `duplicate_applications`: the normalised clean_ref ∥ authority key. For
   *  `possible_duplicate_applications`: the shortest member reference (the "core"
   *  the others extend). */
  group_key: string;
  count: number;
  applications: ReviewAppSummary[];
}

export interface ReviewQueueResponse {
  unmatched_applications: ReviewUnmatchedApp[];
  /** Exact duplicates: same normalised clean_ref + authority (job-agnostic). */
  duplicate_applications: ReviewDuplicateGroup[];
  /** Near-ref duplicates on the SAME job + type + authority — one canonical
   *  reference contains another (e.g. a "B/" prefix or "/A"/"R1" suffix variant)
   *  that exact clean_ref equality misses. Disjoint from `duplicate_applications`. */
  possible_duplicate_applications: ReviewDuplicateGroup[];
  counts: { unmatched: number; duplicate_groups: number; possible_duplicate_groups: number };
}

/** POST application/link_to_job — sets job_id + match_status="matched" on the app
 *  (404 missing app/job; 400 if the job was merged away). */
export interface LinkAppToJobResponse {
  application_id: number;
  job_id: number;
  match_status: string;
}

/* POST application/merge — collapses 2–10 same-type duplicate applications into a
 * winner. Repoints the losers' letters/fees/tasks onto the winner, backfills the
 * winner's empty fields, then tombstones the losers (match_status
 * "merged_into:<id>", closed). Two modes (server-validated):
 *   • standard — all candidates share one non-zero job_id; winner = latest letter.
 *   • orphan-loser (allow_orphan_loser) — exactly one candidate on a real job, the
 *     rest job_id=0; the real-job app is forced winner.
 * `dry_run: true` returns the preview shape (no writes). */

export interface ApplicationMergeRepoint {
  planning_letter: number;
  building_control_letter: number;
  fee: number;
  task: number;
}

/** dry_run=true response — what *would* happen, nothing written. */
export interface ApplicationMergePreview {
  dry_run: true;
  winner_id: number;
  winner_selected_because: string;
  losers_to_merge: number[];
  merge_type: string; // "planning" | "building_control"
  shared_job_id: number;
  allow_orphan_loser: boolean;
  repointed: ApplicationMergeRepoint;
  winner_fields_to_backfill: string[];
  winner_updates_preview: Record<string, unknown>;
}

/** dry_run=false response — the committed merge. */
export interface ApplicationMergeResult {
  dry_run: false;
  winner_id: number;
  winner_selected_because: string;
  losers_merged: number[];
  merge_type: string;
  shared_job_id: number;
  allow_orphan_loser: boolean;
  repointed: ApplicationMergeRepoint;
  winner_backfilled_fields: string[];
  winner: unknown; // full winning application row — not needed for display
}

/* ─── Jobs hub — Fees tab (Brief 04) ───────────────────────────────────────
 * The per-job "order book" of contracted line items, in the Invoicing group
 * `M8XIPbVz` — NOT in the Jobs /detail payload. The Fees tab owns its own
 * fetch/refresh against GET /fees?job_id. amount_invoiced/amount_paid/status are
 * system-managed by the invoicing flow — display only, never written here. */

/** service = VATable (Xero sales code 200); disbursement = non-VAT (code 327).
 *  Named FeeKind to avoid colliding with the quote line-item `FeeType`. */
export type FeeKind = "service" | "disbursement";
/** Derived from amount vs amount_invoiced — server-owned, never set by the UI. */
export type FeeStatus = "pending" | "partially_invoiced" | "fully_invoiced";

export interface Fee {
  id: number;
  job_id: number;
  application_id: number; // 0 = not tied to a specific application
  description: string;
  amount: number; // GBP decimal — the contracted fee amount
  amount_invoiced: number; // system-managed
  amount_paid: number; // system-managed
  fee_type: FeeKind;
  status: FeeStatus; // system-managed, derived
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface FeesSummary {
  count: number;
  total_agreed: number; // Σ amount
  total_invoiced: number; // Σ amount_invoiced
  total_paid: number; // Σ amount_paid
  total_remaining_to_invoice: number; // agreed − invoiced
  total_outstanding: number; // invoiced − paid
}

export interface FeesListResponse {
  items: Fee[]; // sorted created_at asc
  summary: FeesSummary;
}

/** Body for POST {Inv}/fees/create. fee_type defaults to "service". */
export interface FeeCreateInput {
  job_id: number;
  description: string;
  amount: number;
  fee_type?: FeeKind;
  application_id?: number;
  notes?: string;
}

/** Body for PATCH {Inv}/fees/{id} — all optional. `amount` can't go below the
 *  fee's amount_invoiced (server 400). application_id is NOT editable here. */
export interface FeeUpdateInput {
  description?: string;
  amount?: number;
  fee_type?: FeeKind;
  notes?: string;
}

export interface FeeDeleteResponse {
  deleted: true;
  id: number;
}

/* ─── Order book (Jobs › Order book) ───────────────────────────────────────
 * Cross-job list of every LIVE job (merged/archived excluded) that still has
 * uninvoiced fees, for the Order book page. The grand `total` and each job's
 * `remaining` use the IDENTICAL calc as the overview dashboard's order-book KPI
 * — Σ over the job's fees of max(0, fee.amount − fee.amount_invoiced) — so the
 * page total matches the KPI exactly. Served by GET {Jobs}/order_book
 * (staff-gated). */

export interface OrderBookFee {
  id: number;
  description: string;
  fee_type: FeeKind;
  amount: number; // the contracted fee amount
  amount_invoiced: number; // system-managed
  amount_paid: number; // system-managed
  /** amount − amount_invoiced: this fee's contribution to the order book. */
  uninvoiced: number;
  status: FeeStatus;
  application_id: number; // 0 = not tied to a specific application
  notes: string;
  /** False once the fee has been (part-)invoiced — the server DELETE refuses a
   *  fee with invoice lines, so the UI disables its delete button. */
  deletable: boolean;
  created_at: string | number;
}

export interface OrderBookJob {
  job_id: number;
  job_number: number;
  job_name: string;
  full_address: string;
  address_line_1: string;
  address_line_2: string;
  address_line_3: string;
  city: string;
  county: string;
  postcode: string;
  status: string;
  client_name: string;
  agreed: number; // Σ fee.amount
  invoiced: number; // Σ fee.amount_invoiced
  /** Σ max(0, fee.amount − fee.amount_invoiced): this job's order-book total. */
  remaining: number;
  fees: OrderBookFee[]; // the uninvoiced fees, oldest-first
}

export interface OrderBookResponse {
  /** Σ remaining across jobs — matches the dashboard order-book KPI value. */
  total: number;
  jobs_count: number; // number of jobs contributing a positive remainder
  jobs: OrderBookJob[]; // sorted by remaining desc
}

/* ─── Jobs hub — Invoices tab (Brief 05) ───────────────────────────────────
 * Invoices bill fees and raise LIVE, AUTHORISED Xero invoices — create is
 * irreversible (confirm-gate + no double-submit). Invoice state/payments are
 * Xero-owned and synced in via Make; the UI only creates + lists, never PATCHes
 * an invoice. Lives in the Invoicing group; not in /detail. Dates come back as
 * epoch ms (typed string|number for formatDateShort/Date()). */

export interface InvoiceLine {
  id: number;
  invoice_id: number;
  fee_id: number | null; // legacy lines can be null
  description: string;
  amount: number;
  fee_type: FeeKind;
  xero_line_id: string;
}

export interface Invoice {
  id: number;
  job_id: number;
  client_id: number;
  xero_invoice_id: string;
  xero_invoice_number: string;
  xero_url: string; // "View in Xero"
  online_invoice_url: string; // client-facing payment link
  status: string; // Xero-owned, display only (draft|submitted|awaiting_payment|paid|voided|deleted)
  total: number;
  amount_paid: number;
  amount_outstanding: number;
  issue_date: string | number;
  due_date: string | number;
  paid_date: string | number | null;
  lines: InvoiceLine[];
}

export interface InvoicesSummary {
  count: number;
  total_billed: number;
  total_paid: number;
  total_outstanding: number;
}

export interface InvoicesListResponse {
  items: Invoice[]; // sorted issue_date desc, lines embedded
  summary: InvoicesSummary;
}

/** One billed-fee line for POST {Inv}/invoices/create. `amount` must be > 0 and
 *  ≤ the fee's remaining (amount − amount_invoiced). */
export interface InvoiceCreateLine {
  fee_id: number;
  amount: number;
  description?: string;
}

export interface InvoiceCreateInput {
  job_id: number;
  lines: InvoiceCreateLine[];
  issue_date?: string;
  due_date?: string;
  reference?: string;
}

export interface InvoiceCreateResponse {
  invoice: Invoice;
  xero_url: string;
  online_invoice_url: string;
  xero_invoice_number: string;
  total: number;
}

/* ─── Cross-job Invoices dashboard (Invoices page) ─────────────────────────
 * GET {Inv}/dashboard — KPIs + enriched, flat, cross-job invoice rows in one
 * round-trip. Read-only; the page links out to Xero (never writes status).
 * Verified against live 2026-06-18 (dates are epoch ms). */

export interface InvoiceKpis {
  total_outstanding: number;
  total_overdue: number;
  overdue_count: number;
  mtd_billed: number;
}

/** Enriched cross-job row (distinct from the per-job Invoice — flat, no lines). */
export interface InvoiceDashboardRow {
  id: number;
  job_id: number;
  job_number: number;
  job_name: string | null;
  client_id: number | null;
  client_name: string | null;
  xero_invoice_id: string;
  xero_invoice_number: string;
  firebase_invoice_id: string | null;
  online_invoice_url: string;
  xero_url: string;
  total: number;
  balance: number;
  amount_paid: number;
  status: string;
  issue_date: string | number;
  due_date: string | number;
  paid_date: string | number | null;
  days_to_due: number | null; // negative = overdue
  is_overdue: boolean;
  is_outstanding: boolean;
  issued_this_month: boolean;
}

export interface InvoiceDashboardResponse {
  kpis: InvoiceKpis;
  invoices: InvoiceDashboardRow[];
  as_of_date: string | number;
  as_of_ym: string;
}

/* ─── Letters ──────────────────────────────────────────────────────────────
 * Letters group `S2Lb2uzv`. Surfaced read-only inside the Applications tab
 * (Brief 06) and as the standalone Letters Inbox (with read-receipts keyed on
 * the Xano user.id via `user_id`). NOTE (verified live): /list REQUIRES
 * include_read_days >= 1 — the API module always sends it. */

export type LetterSource = "planning" | "building_control";
export type LetterConfidence = "high" | "medium" | "low";

export interface LetterListRow {
  id: number;
  source: LetterSource;
  created_at: string;
  letter_type: string | null;
  letter_subtype: string | null;
  letter_date: string | null;
  confidence: LetterConfidence | null;
  source_document_title: string | null;
  addressed_to: string | null;
  addressed_to_role: string | null;
  application_id: number | null;
  application_reference: string | null;
  application_type: string | null;
  application_status: string | null;
  job_id: number | null;
  job_name: string | null;
  task_id: number | null;
  task_title: string | null;
  task_status: string | null;
  has_linked_task: boolean;
  is_read: boolean; // per the user_id passed to /list
}

export interface LetterListResponse {
  items: LetterListRow[];
  paging: { page: number; per_page: number; total: number; total_pages: number; has_more: boolean };
}

export interface LetterListFilters {
  application_id?: number;
  job_id?: number;
  source?: LetterSource;
  letter_types?: string;
  q?: string;
  date_from?: string;
  date_to?: string;
  sort_field?: "letter_date" | "letter_type" | "application_reference" | "confidence" | "source" | "job_name";
  sort_direction?: "asc" | "desc";
  page?: number;
  per_page?: number;
  /** Drives per-row is_read + the unread_only filter. The Xano user.id (from
   *  auth/me); the backend resolves receipts by it. */
  user_id?: number;
  unread_only?: boolean;
  /** When unread_only, also keep read letters from the last N days (1–365).
   *  Also satisfies the endpoint's mandatory include_read_days >= 1. */
  include_read_days?: number;
}

/** GET {Let}/unread-count — lightweight badge source (per the user's receipts). */
export interface LetterUnreadCount {
  total: number;
  planning: number;
  building_control: number;
}

/** POST {Let}/mark-all-read — counts of receipts the call actually created
 *  (already-read letters are skipped, so a repeat call returns zeroes). */
export interface LetterMarkAllReadResult {
  marked_planning: number;
  marked_building_control: number;
  total_marked: number;
}

/* ─── Letters › Duplicate review (Review › Letters workbench) ───────────────
 * Detect + soft-archive duplicate letters (the same document parsed twice on
 * re-ingest). `letters/duplicates` groups ACTIVE letters within one table by
 * application + letter date + type; "exact" groups share an identical non-empty
 * title (near-certain re-ingest), "possible" groups differ on title. `letters/
 * merge` keeps one letter and soft-archives the rest (sets merged_into, so they
 * drop out of the inbox + this queue but stay auditable), repointing the losers'
 * read-receipts onto the keeper and inheriting a planning letter's task link.
 * Letters group `S2Lb2uzv`. */

export interface LetterDuplicateMember {
  id: number;
  source: LetterSource;
  /** Ingestion time (epoch ms) — tells you which copy landed first. */
  created_at: number;
  letter_type: string | null;
  letter_subtype: string | null;
  /** Date on the letter (epoch ms). */
  letter_date: number | null;
  confidence: LetterConfidence | null;
  source_document_title: string | null;
  addressed_to: string | null;
  /** The letter's own LPA/BCB reference. */
  app_reference: string | null;
  application_id: number;
  /** The linked application's reference (may differ from the letter's). */
  application_reference: string | null;
  application_status: string | null;
  job_id: number; // 0 = unlinked
  job_name: string | null;
  task_id: number; // planning letters only; 0 = none
  /** Open both members' PDFs to eyeball that they're the same document. */
  pdf_url: string | null;
  pdf_filename: string | null;
}

export interface LetterDuplicateGroup {
  /** Human label, e.g. "FP/22/0610 · 2022-11-15 · plans_decision". */
  group_key: string;
  source: LetterSource;
  application_id: number;
  count: number;
  /** true = every member shares an identical non-empty title (near-certain
   *  re-ingest); false = same app/date/type but the title differs (review). */
  exact: boolean;
  letters: LetterDuplicateMember[];
}

export interface LetterDuplicatesResponse {
  /** Exact duplicates — shared non-empty source_document_title. */
  duplicate_letters: LetterDuplicateGroup[];
  /** Possible duplicates — same app/date/type, title differs. */
  possible_duplicate_letters: LetterDuplicateGroup[];
  counts: { duplicate_groups: number; possible_duplicate_groups: number };
}

/** POST letters/merge with dry_run=true — the no-write plan. */
export interface LetterMergePreview {
  dry_run: true;
  source: LetterSource;
  application_id: number;
  keeper_id: number;
  /** "most_recent_letter_date" | "chosen_by_user". */
  keeper_selected_because: string;
  losers_to_merge: number[];
  task_inherited_from_letter_id: number; // 0 = none
  task_inherited_id: number; // 0 = none
  read_receipts_to_repoint: number;
}

/** POST letters/merge — the committed soft-archive. */
export interface LetterMergeResult {
  dry_run: false;
  source: LetterSource;
  application_id: number;
  keeper_id: number;
  keeper_selected_because: string;
  losers_merged: number[];
  task_inherited_from_letter_id: number;
  task_inherited_id: number;
  read_receipts_repointed: number;
  keeper: unknown; // full keeper row — not needed for display
}

/** The parser's full structured output. Conforms to the canonical schemas
 *  (planning v1.5 / BC v1.2) but parsed against a range of versions — every
 *  section is optional; the renderer treats all keys as best-effort. */
export interface LetterRawJson {
  letter?: Record<string, unknown>;
  decision?: Record<string, unknown>;
  dates?: Record<string, unknown>;
  fee?: Record<string, unknown>;
  conditions?: Array<Record<string, unknown>>;
  outstanding_items?: Array<Record<string, unknown>>;
  questions_from_planner?: Array<Record<string, unknown>>;
  informatives?: Array<Record<string, unknown>>;
  flags?: Record<string, unknown>;
  cil?: Record<string, unknown>;
  next_actions?: Array<Record<string, unknown>>;
  links?: Record<string, unknown>;
  approved_documents?: Record<string, unknown>;
  appeal_rights?: Record<string, unknown>;
  working_hours_restriction?: Record<string, unknown>;
  // building-control-specific
  regulation_parts?: Record<string, unknown>;
  inspection_stages?: Record<string, unknown>;
  inspection_records?: Array<Record<string, unknown>>;
  duty_holders?: Record<string, unknown>;
  design_review_items?: Array<Record<string, unknown>>;
  energy_compliance?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface LetterDetail {
  id: number;
  source: LetterSource;
  created_at: string;
  letter_type: string | null;
  letter_subtype: string | null;
  letter_date: string | null;
  confidence: LetterConfidence | null;
  source_document_title: string | null;
  addressed_to: string | null;
  addressed_to_role: string | null;
  raw_json: LetterRawJson;
  pdf_url: string | null;
  pdf_filename: string | null;
  application: {
    id: number;
    app_reference: string | null;
    application_type: string | null;
    status: string | null;
    decision_outcome: string | null;
    local_authority_or_bcb: string | null;
    job_id: number | null;
  } | null;
  job: { id: number; job_number: number; job_name: string | null; full_address: string | null } | null;
  linked_task: {
    id: number;
    title: string;
    status: string;
    priority: string;
    due_date: string | null;
    deleted_at: string | null;
  } | null;
}

/* ─── Studio overview dashboard ─────────────────────────────────────────────
 * GET {dashboard}/dashboard/summary — one call populates the whole page.
 * All money values are GBP numbers (may be decimal); all *_start / now / today
 * / generated_at / month fields are epoch-ms. `placeholder: true` on the leads/
 * quotes blocks means those pipelines aren't live yet — render from the SPA's
 * local sample dataset (the empty arrays are expected, per-block switch). */

export interface DashboardHeroDelta {
  value: number;
  prev: number;
  delta_pct: number;
  placeholder?: boolean;
}
export interface DashboardHero {
  new_leads: DashboardHeroDelta;
  quotes_sent: { value: number; win_rate: number; placeholder?: boolean };
  active_jobs: { value: number; prev: number; delta_abs: number };
  invoiced: DashboardHeroDelta;
  outstanding: { value: number; count: number; overdue_count: number };
  /** Order book = current uninvoiced fees: Σ over live jobs (merged/archived
   *  excluded) of max(0, total_agreed − total_invoiced), clamped per job so it
   *  reconciles with the jobs-list `remaining_to_invoice` column. `jobs` =
   *  number of jobs contributing a positive remainder. */
  order_book: { value: number; jobs: number };
}

export interface DashboardFunnelStage { stage: string; label: string; count: number }
export interface DashboardLeadSource { source: string; leads: number; won: number; conversion_pct: number }
export interface DashboardLeadsMonth { month: number; count: number }
export interface DashboardQuotesMonth { month: number; sent: number; win_rate: number }

export interface DashboardLeads {
  placeholder?: boolean;
  this_month: number;
  ytd: number;
  monthly: DashboardLeadsMonth[];
  funnel: DashboardFunnelStage[];
  sources: DashboardLeadSource[];
}
export interface DashboardQuotes {
  placeholder?: boolean;
  this_month: number;
  ytd: number;
  win_rate: number;
  monthly: DashboardQuotesMonth[];
}

export interface DashboardJobStage { status: string; label: string; count: number }
export interface DashboardInvoicedMonth { month_start: number; value: number }
export interface DashboardAgedBand { band: string; amount: number }
export interface DashboardTaskPriority { priority: string; count: number }

/** Deadline tracker is parked upstream (always `[]` for now); the card renders
 *  this shape when the array fills. `days_to_decision` negative = overdue. */
export interface DashboardDeadlineRow {
  application_id: number;
  app_reference: string;
  authority: string;
  date_statutory_deadline: number;
  days_to_decision: number;
  rag: "overdue" | "warning" | "ok";
}

export interface DashboardSummary {
  generated_at: number;
  period: { now: number; today: number; month_start: number };
  hero: DashboardHero;
  leads: DashboardLeads;
  quotes: DashboardQuotes;
  jobs: { active_total: number; by_stage: DashboardJobStage[] };
  applications: { deadline_tracker: DashboardDeadlineRow[] };
  invoices: {
    invoiced_this_month: number;
    outstanding_total: number;
    outstanding_count: number;
    monthly_invoiced: DashboardInvoicedMonth[];
    aged_receivables: DashboardAgedBand[];
  };
  tasks: { open_total: number; overdue_count: number; by_priority: DashboardTaskPriority[] };
}

export interface DashboardSummaryFilters {
  trend_months?: number;
  tracker_limit?: number;
}

/* ─── KPI history (kpi_snapshot table → GET kpi/history, dashboard group) ────
 * Daily point-in-time snapshots of a tracked KPI, written overnight by the
 * kpi_snapshot_daily task (one row per KPI per UTC day). Generic by `kpi_key`
 * (e.g. "order_book") so any KPI can be tracked over time; `meta` carries the
 * per-KPI extras (e.g. {jobs} for the order book). Points are oldest-first. */
export interface KpiHistoryPoint {
  /** UTC business day the snapshot represents, "YYYY-MM-DD". */
  date: string;
  value: number;
  meta?: Record<string, unknown> | null;
}

export interface KpiHistoryResponse {
  kpi_key: string;
  points: KpiHistoryPoint[];
}

/* ─── Accepted Quote Workbench (Quote group `EBXrikCU`) ─────────────────────
 * The provision plan is the server's read-only proposal (job/client match
 * candidates + the editable "new" forms + fee/drawing seeds). The user edits a
 * working copy and commits once via the ProvisionRequest action envelope. */

/** `project_description` is the value behind the `{{short_description}}` token —
 *  the workbench resolves line-item text against it when seeding fee rows. */
export interface ProvisionPlanQuote { id: number; quote_number: string; status: string; drawing_type: string; accepted_total: number; project_description?: string | null; }
export interface ProvisionAcceptedLine { id: number; title: string; description: string; line_total: number; fee_type: string; drawing_type: "none" | "planning" | "building_control" | "other"; position: number; }
export interface ProvisionJobCandidateSet { id: number; set_number: number; label: string; header_status: string; is_complete: boolean; drawing_count: number; }
export interface ProvisionJobCandidate { id: number; job_name: string; full_address: string; postcode: string; score: number; match_reason: string; current_drawing_set: ProvisionJobCandidateSet | null; }
export interface ProvisionProposedJob { job_name: string; full_address: string; postcode: string; address_line_1: string; address_line_2: string; address_line_3: string; city: string; county: string; district: string; lat: number; lng: number; uprn: string; planning_in_scope: boolean; bc_in_scope: boolean; other_in_scope: boolean; status: string; }
export interface ProvisionClientCandidate { id: number; label: string; email: string; postcode: string; score: number; match_reason: string; }
export interface ProvisionProposedClient { is_company: boolean; company_name: string; contact_first_name: string; contact_last_name: string; email: string; phone: string; address_line_1: string; city: string; county: string; postcode: string; }
export interface ProvisionFeeSuggestion { source_quote_line_id: number; default_action: "create"; proposed: { description: string; amount: number; fee_type: string; status: string }; }
export interface ProvisionSeedDrawing { source_quote_line_id: number; drawing_type: "planning" | "building_control" | "other"; title: string; }
export interface ProvisionPlan {
  quote: ProvisionPlanQuote;
  accepted_lines: ProvisionAcceptedLine[];
  suggestions: {
    job: { default_action: "create" | "link"; candidates: ProvisionJobCandidate[]; proposed_new: ProvisionProposedJob };
    client: { default_action: "create" | "link"; candidates: ProvisionClientCandidate[]; proposed_new: ProvisionProposedClient };
    fees: ProvisionFeeSuggestion[];
    drawing_set: { default_action: "create"; proposed: { label: string }; seed_drawings: ProvisionSeedDrawing[] };
  };
}

/* Commit request — the user's chosen actions per card. */
export type ProvisionJobAction = { action: "create"; data: ProvisionProposedJob } | { action: "link"; job_id: number };
export type ProvisionClientAction = { action: "create"; data: ProvisionProposedClient } | { action: "link"; client_id: number } | { action: "inherit" } | { action: "skip" };
export type ProvisionFeeAction = { action: "create"; source_quote_line_id: number; data: { description: string; amount: number; fee_type: string } } | { action: "skip"; source_quote_line_id: number };
export type ProvisionDrawingSetAction = { action: "create" | "close_and_new"; data: { label: string }; seed: { drawing_type: string; title: string }[] } | { action: "keep" } | { action: "skip" };
export interface ProvisionRequest { job: ProvisionJobAction; client: ProvisionClientAction; fees: ProvisionFeeAction[]; drawing_set: ProvisionDrawingSetAction; }
export interface ProvisionResult { quote_id: number; status: string; created: { client_id: number; job_id: number; job_client_id: number; fee_ids: number[]; drawing_set_id: number; drawing_ids: number[]; lead_ids: number[] }; closed_drawing_set_id: number; skipped: string[]; }
export interface QuoteAcceptResponse { quote_id: number; status: string; accepted_at: number; }

/* ─── Feefo review-request lifecycle (Feedback tab) ─────────────────────────
 * A feefo_request row is the local source of truth for one review invitation:
 * "requested → sent → responded". Feefo only ever tells us about the "responded"
 * side (a review matched back by order_reference); the sent/awaiting state is
 * ours. Timestamps are epoch-ms, 0 when unset (mirror the Xano `> 0` convention). */
export type FeefoRequestStatus = "sent" | "responded" | "failed";

export interface FeefoRequest {
  id: number;
  created_at: number;
  job_id: number;
  /** Feefo de-dup / matching key — `JOB-{job_number}-{request_seq}`. */
  order_reference: string;
  /** Monotonic per-job counter (1, 2, 3…) — the integer in the order ref. */
  request_seq: number;
  /** Descriptive label only (drawings_complete | permission_granted | reminder |
   *  manual) — NOT part of the key. */
  milestone: string;
  client_email: string;
  client_name: string;
  description: string;
  status: FeefoRequestStatus;
  sent_at: number;
  future_feedback_date: number | null;
  /** > 0 once a review has been matched back. */
  response_received_at: number;
  review_id: string;
  review_rating: number;
  review_title: string;
  review_text: string;
  review_url: string;
  review_created_at: number;
  last_checked_at: number;
  /** Optional free-text stage/reason note captured at send time (history only). */
  stage_note: string;
  raw_review?: unknown;
}

export interface FeefoRequestsResponse {
  items: FeefoRequest[];
  summary: { count: number; responded_count: number; average_rating: number };
}

/** POST /feefo/check response — the refreshed list plus how many were polled/updated. */
export interface FeefoCheckResponse {
  items: FeefoRequest[];
  checked: number;
  updated: number;
}

export interface FeefoRequestInput {
  job_id: number;
  /** Descriptive milestone label (defaults to "manual" server-side). */
  milestone?: string;
  /** Feefo "description" (defaults to "Architectural design services"). */
  description?: string;
  /** Optional free-text stage/reason note for the history. */
  stage_note?: string;
}

/* Cross-job feeds for the standalone Feedback section (Feefo group). */
export interface FeefoOpenItem {
  id: number;
  job_id: number;
  job_number: number;
  job_name: string;
  order_reference: string;
  request_seq: number;
  milestone: string;
  client_email: string;
  client_name: string;
  description: string;
  status: FeefoRequestStatus;
  sent_at: number;
  response_received_at: number;
  last_checked_at: number;
  stage_note: string;
}

export interface FeefoReceivedItem {
  id: number;
  job_id: number;
  job_number: number;
  job_name: string;
  order_reference: string;
  request_seq: number;
  milestone: string;
  client_email: string;
  client_name: string;
  status: FeefoRequestStatus;
  sent_at: number;
  response_received_at: number;
  review_id: string;
  review_rating: number;
  review_title: string;
  review_text: string;
  review_url: string;
  review_created_at: number;
  stage_note: string;
}

/** One job match from the feedback job picker (carries the resolved client). */
export interface FeefoJobSearchResult {
  job_id: number;
  job_number: number;
  job_name: string;
  full_address: string;
  postcode: string;
  client_name: string;
  client_email: string;
  has_email: boolean;
}

/* ── Staff chat & documents (Staff Chat Brief 01) ──────────────────────── */

/** "builder" since 2026-09-21 (Builders Build Brief 01): a portal login that
 *  belongs to a builder firm attached to the job. */
export type ChatSenderType = "staff" | "client" | "builder";

/** One row of GET {Jobs}/staff/chat/jobs — the feed for both chat sidebars and
 *  the nav badge. Job set = jobs reachable via an active, non-review portal
 *  login. Sorted server-side: active threads by recency, then no-conversation
 *  jobs alphabetically. `job_number` can be 0 (the App Store demo job). */
export interface ChatJob {
  job_id: number;
  job_number: number;
  name: string;
  /** Deduplicated display names of the job's clients, comma-joined. */
  client_names: string;
  /** Deduplicated firm names of the builders on the job, comma-joined; "" when
   *  none. Absent on rows fetched before 2026-09-21. */
  builder_names?: string;
  last_message_at: number; // epoch ms; 0 = no conversation yet
  last_message_snippet: string;
  last_message_sender_type: ChatSenderType | "";
  /** Staff author of the last message (0 for client messages and legacy rows) —
   *  lets the sidebar say "You:" only when it really was you (D6, 2026-09-04). */
  last_message_sender_user_id: number;
  /** Resolved display name of the last message's author ("" when unresolvable). */
  last_message_sender_name: string;
  /** Client messages unread by the CALLING staff user (per-user read state). */
  unread_count: number;
}

/** One attachment on a chat message (image/* or application/pdf, ≤20 MB —
 *  server-enforced). Same shape returned by POST staff/chat/upload; an upload
 *  stays staged (invisible, download-url 404s) until a send links it. */
export interface ChatAttachment {
  attachment_id: number;
  file_name: string;
  mime_type: string;
  file_size: number;
  /** PDFs only: a first-page preview JPEG exists (fetch it via download-url
   *  with thumb=true). Absent until the console has made one. */
  has_thumb?: boolean;
}

/** One message from GET/POST {Jobs}/staff/chat/{job_id}/messages. Messages
 *  predating attachments carry ""/null in `attachments` on the wire — the api
 *  seam normalizes that to [] before it reaches components. */
export interface ChatMessage {
  id: number;
  sender_type: ChatSenderType;
  /** Staff author of a staff row (0 on client rows). The "mine" key for the
   *  edit/delete controls — never key on sender_type alone (a colleague's
   *  message is theirs). Absent on rows fetched before 2026-09-04. */
  sender_user_id?: number;
  sender_name: string;
  body: string;
  attachments: ChatAttachment[];
  created_at: number;
  /** When the author last edited the body; 0 or absent = never. */
  edited_at?: number;
  /** When the client side first had the thread on screen after this staff
   *  message existed (stamped by the portal's thread fetch). Side-level, not
   *  per-person: on a job with two portal users the first reader stamps it —
   *  it means "someone client-side has seen it". 0 = not yet seen; absent on
   *  rows fetched before receipts shipped. Meaningful on staff rows only. */
  client_read_at?: number;
}

/* ── Chat history import (Moxo migration) ──────────────────────────────── */

/** One selectable identity from GET staff/chat/import/participants. */
export interface ChatImportStaffOption {
  user_id: number;
  name: string;
}

export interface ChatImportClientOption {
  portal_user_id: number;
  name: string;
  email: string;
  /** Historic senders may be deactivated today — still valid to map onto. */
  is_active: boolean;
}

/** A job client with no portal_user yet — offered as a "create portal access"
 *  mapping: importing then provisions the record via POST portal/invite with
 *  send_invite_email=false (verified: the explicit false travels; nothing is
 *  emailed). `name` is derived exactly as the invite flow derives it, so it is
 *  the name the created record will carry. */
export interface ChatImportCreatableClient {
  client_id: number;
  name: string;
  email: string;
  /** false = the client record has no email; a portal_user cannot be created
   *  until one is added. */
  has_email: boolean;
}

/** GET {Jobs}/staff/chat/import/participants?job_id — the mapping palette for
 *  one job, plus this job's prior import runs: their `last_seq` /
 *  `last_message_at` are the high-water marks a re-export of the same board is
 *  diffed against (update mode). */
export interface ChatImportParticipants {
  staff: ChatImportStaffOption[];
  clients: ChatImportClientOption[];
  clients_without_portal: ChatImportCreatableClient[];
  /** Builder logins whose firm is attached to this job (review/test hidden). */
  builders?: ChatImportBuilderOption[];
  /** Attached firms with no login yet — offered as "create builder access". */
  builders_without_portal?: ChatImportCreatableBuilder[];
  existing_message_count: number;
  prior_imports: {
    board_id: string;
    board_name: string;
    imported_at: number;
    message_count: number;
    /** Highest Moxo seq that run imported (0 = unknown → diff by timestamp). */
    last_seq: number;
    last_message_at: number;
  }[];
}

/** One message in the POST staff/chat/import/commit payload — already mapped
 *  to a concrete sender by the Import History page. */
export interface ChatImportMessageInput {
  at: number; // original send time, epoch ms
  sender_kind: ChatSenderType;
  sender_user_id: number; // staff author; 0 for client messages
  sender_portal_user_id: number; // client author; 0 for staff messages
  body: string;
  attachment_ids: number[];
}

/** POST staff/chat/import/commit response. */
export interface ChatImportCommitResult {
  import_id: number;
  conversation_id: number;
  imported_messages: number;
  imported_attachments: number;
  first_message_at: number;
  last_message_at: number;
}

/** One row of GET staff/chat/imports — the Import History run log. An update
 *  run (append) is its own row, so counts are per run, not per board. */
export interface ChatImportRun {
  id: number;
  imported_at: number;
  job_id: number;
  job_number: number;
  job_name: string;
  board_id: string;
  board_name: string;
  message_count: number;
  attachment_count: number;
  first_message_at: number;
  last_message_at: number;
  /** Highest Moxo seq that run imported (0 = unknown). */
  last_seq: number;
  imported_by: string;
}

/** document_share lifecycle (client portal as-built §2.1). Raw values never
 *  render — map through shareStatusToPill. */
export type ShareStatus =
  | "draft"
  | "sent"
  | "viewed"
  | "approved"
  | "changes_requested"
  | "superseded"
  | "withdrawn";

/** The latest immutable decision on a share (S5 `latest_decision`). */
export interface ShareDecision {
  decision: "approved" | "changes_requested";
  decided_by_name: string;
  comment: string;
  decided_at: number;
}

/** One row of GET {Jobs}/portal/shares?job_id (S5) — staff view: all statuses
 *  with file facts and the latest decision. */
export interface DocumentShare {
  share_id: number;
  job_id: number;
  drawing_id: number;
  title: string;
  status: ShareStatus;
  message: string;
  message_from: string;
  /** false = "for information only" (as-built §9.13): the client can view but
   *  is never asked to decide — no due date, no approval action, no decision. */
  requires_approval: boolean;
  on_approval_action: string;
  supersedes_share_id: number;
  due_by: number; // epoch ms; 0 = none
  sent_at: number;
  viewed_at: number;
  decided_at: number;
  created_at: number;
  created_by: string;
  file_name: string;
  page_count: number;
  file_size: number;
  latest_decision: ShareDecision | null;
}

/** Raw document_share row returned by the S1/S2/S3 write endpoints (note `id`,
 *  not `share_id` — only S5 renames). */
export interface ShareRow {
  id: number;
  status: ShareStatus;
  job_id: number;
  drawing_id: number;
}

/** Body for POST {Jobs}/portal/share (S1). One drawing per share; identity
 *  fields (message_from/created_by) are filled from the staff session. */
export interface ShareCreateInput {
  job_id: number;
  drawing_id: number;
  title?: string;
  message?: string;
  /** Defaults true server-side; false = for-information (S1 then rejects an
   *  approval action and zeroes due_by). */
  requires_approval?: boolean;
  due_by?: number;
  supersedes_share_id?: number;
}

/** The drawing row returned by POST {Jobs}/portal/drawing/upload (292) after
 *  the R2 PUT + metadata patch. Only the fields the UI needs. */
export interface UploadedDrawing {
  id: number;
  drawing_set_id: number;
  file_name?: string;
}

/** `{url, expires_in}` from GET {Jobs}/staff/drawing/{id}/download-url. Fetch
 *  at view time and use immediately — presigned URLs expire (300 s). */
export interface DownloadUrl {
  url: string;
  expires_in: number;
}

/** The plain container set created by POST {Jobs}/staff/jobs/{job_id}/drawing_sets
 *  (non-current, no seeded drawings — NOT the create_drawing_set workflow). */
export interface DrawingSetContainer {
  id: number;
  job_id: number;
  set_number: number;
  label: string;
}

/* ── Site photos (Job Detail › Photos tab) ────────────────────────────────
 * Bytes live in the client-docs R2 bucket (original + browser-made thumbnail);
 * Xano t photo_set / site_photo are the truth about sets and taken dates. See
 * docs/site_photos_build_brief.md. */

/** One phase / visit — the selector chips on the Photos tab. From GET
 *  {Jobs}/staff/photos/sets and inside GET {Jobs}/staff/photos. */
export interface PhotoSet {
  id: number;
  job_id: number;
  label: string;
  /** The day of the visit: midnight UTC, epoch ms (the SPA's date convention). */
  visit_date: number;
  /** 0 = not linked to a drawing set. */
  drawing_set_id: number;
  /** Live (confirmed, undeleted) photos in the set. */
  photo_count: number;
  created_at: number;
}

/** One live photo from GET {Jobs}/staff/photos. `thumb_url` is a presigned
 *  URL good for `thumb_expires_in` seconds (1 h) — on an img error re-fetch
 *  the list rather than retrying the URL. The original is signed on demand via
 *  getPhotoDownloadUrl. */
export interface SitePhoto {
  id: number;
  photo_set_id: number;
  file_name: string;
  mime_type: string;
  file_size: number;
  /** Oriented pixel size of the original (0 when unknown). */
  width: number;
  height: number;
  /** When it was taken — EXIF DateTimeOriginal read in the browser, else the
   *  file's modified time. Local-time epoch ms. */
  taken_at: number;
  caption: string;
  uploaded_at: number;
  /** Uploader's display name ("" when unresolved). */
  uploaded_by: string;
  /** false = the photo arrived without a thumbnail (MCP upload that skipped
   *  it, URL import) and `thumb_url` is the ORIGINAL. The Photos tab makes
   *  the thumbnail itself when it sees one of these. */
  has_thumb: boolean;
  thumb_url: string;
}

/** The photo row as returned by PATCH {Jobs}/staff/photos/{id} (no URL, no
 *  uploader name — merge it over the list copy). */
export type SitePhotoRow = Omit<SitePhoto, "thumb_url" | "uploaded_by" | "has_thumb">;

export interface PhotosResponse {
  sets: PhotoSet[];
  photos: SitePhoto[];
  thumb_expires_in: number;
}

/** Body for POST {Jobs}/staff/photos/sets. visit_date is normalised to
 *  midnight UTC server-side and defaults to today; label defaults to
 *  "Site visit {d M Y}". */
export interface PhotoSetCreateInput {
  job_id: number;
  label?: string;
  visit_date?: number;
  drawing_set_id?: number;
}

/** Body for PATCH {Jobs}/staff/photos/sets/{id}. Only sent fields are written;
 *  unlinking the drawing set rides `clear_drawing_set` (an int 0 never reaches
 *  a Xano stack). */
export interface PhotoSetUpdateInput {
  label?: string;
  visit_date?: number;
  drawing_set_id?: number;
  clear_drawing_set?: boolean;
}

/** One file as described to POST {Jobs}/staff/photos/upload-init. `taken_day`
 *  is the LOCAL calendar day of taken_at ("YYYY-MM-DD") — the server groups
 *  auto-created sets on it, so the day is decided where the photo was taken,
 *  not by the server's clock. */
export interface PhotoUploadFileDescriptor {
  file_name: string;
  mime_type: string;
  file_size: number;
  width: number;
  height: number;
  taken_at: number;
  taken_day: string;
}

/** One pending row + its two presigned PUT URLs (15 min). PUT the original to
 *  `put_url` and the thumbnail to `thumb_put_url`, then confirm. */
export interface PhotoUploadTicket {
  id: number;
  photo_set_id: number;
  file_name: string;
  taken_at: number;
  put_url: string;
  thumb_put_url: string;
}

export interface PhotoUploadInitResponse {
  uploads: PhotoUploadTicket[];
  expires_in: number;
}

/** Body for PATCH {Jobs}/staff/photos/{id}. Clearing the caption rides
 *  `clear_caption` (an optional text sent as "" reaches the stack as null). */
export interface PhotoUpdateInput {
  caption?: string;
  clear_caption?: boolean;
  photo_set_id?: number;
  taken_at?: number;
}

/* ── Builders (Builders Build Brief 01, 2026-09-21) ─────────────────────────
 * A builder is a FIRM (`builder`); a person at the firm who can sign in to the
 * portal is a portal_user carrying builder_id. `job_builder` links a firm to a
 * job with a role and a chat-visibility cutoff. Builders see chat only — never
 * documents or approvals — by construction of the entitlement functions. */

export type JobBuilderRole = "appointed" | "tendering" | "referred";
export const JOB_BUILDER_ROLES: readonly JobBuilderRole[] = ["appointed", "tendering", "referred"] as const;

/** Full firm row (GET builders/detail `builder`, POST builders/create, PATCH
 *  builders/{id}). Empty strings for unset text, per the Xano defaults. */
export interface BuilderRecord {
  id: number;
  created_at: number;
  company_name: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_role: string;
  email: string;
  phone: string;
  trade: string;
  website: string;
  notes: string;
  status: string;
}

/** One row of GET {Jobs}/builders/list. */
export interface BuilderListRow {
  id: number;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  trade: string;
  /** Live job links. */
  job_count: number;
  /** Portal logins at the firm (any state). */
  people_count: number;
  is_archived: boolean;
  status: string;
  created_at: number;
}

export interface BuildersListResponse {
  items: BuilderListRow[];
  total: number;
}

/** A job the firm is or was attached to (GET builders/detail `jobs`). */
export interface BuilderJobRow {
  job_builder_id: number;
  job_id: number;
  job_number: number;
  job_name: string;
  address: string;
  postcode: string;
  status: string;
  role: JobBuilderRole | string;
  /** Chat cutoff, epoch ms; 0 = full history. */
  chat_from: number;
  is_active: boolean;
  added_at: number;
  removed_at: number;
}

/** One portal login at a firm (GET builders/detail `people`). The row IS the
 *  invitation; `has_signed_in` separates an acted-on invite from an unopened one. */
export interface BuilderPerson {
  portal_user_id: number;
  name: string;
  email: string;
  is_active: boolean;
  has_signed_in: boolean;
  last_login_at: number;
  invited_at: number;
}

export interface BuilderDetailResponse {
  builder: BuilderRecord;
  jobs: BuilderJobRow[];
  people: BuilderPerson[];
}

/** POST builders/create — company_name is the only required field. */
export interface BuilderCreateInput {
  company_name: string;
  contact_first_name?: string;
  contact_last_name?: string;
  contact_role?: string;
  email?: string;
  phone?: string;
  trade?: string;
  website?: string;
  notes?: string;
}

/** PATCH builders/{id} — presence-based: a PRESENT field is written (including
 *  "" which clears it); an omitted field is untouched. company_name may change
 *  but never blank. */
export type BuilderUpdateInput = Partial<BuilderCreateInput>;

/** POST builders/{id}/invite. Same semantics as PortalInviteResponse: access is
 *  granted even when no email went out. */
export interface BuilderInviteResponse {
  portal_user_id: number;
  email: string;
  name: string;
  builder_id: number;
  is_active: boolean;
  last_login_at: number;
  created: boolean;
  email_sent: boolean;
  email_reason: string;
}

/** A person at the firm as returned inside the job-detail builders block. */
export interface JobBuilderPerson {
  portal_user_id: number;
  name: string;
  email: string;
  is_active: boolean;
  has_signed_in: boolean;
  last_login_at: number;
}

/** One firm on a job — GET {Jobs}/detail `builders[]` (live links only). */
export interface JobBuilder {
  job_builder_id: number;
  builder_id: number;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  trade: string;
  role: JobBuilderRole | string;
  /** Chat cutoff, epoch ms; 0 = full history. */
  chat_from: number;
  added_at: number;
  people: JobBuilderPerson[];
}

/** POST staff/jobs/{job_id}/builders. */
export interface JobBuilderAttachInput {
  builder_id: number;
  role?: JobBuilderRole;
  /** true ⇒ chat_from = 0 (the whole thread); default: from now. */
  show_full_history?: boolean;
}

/** PATCH staff/jobs/{job_id}/builders/{builder_id} — bool flags, never an int 0. */
export interface JobBuilderLinkUpdateInput {
  role?: JobBuilderRole;
  show_full_history?: boolean;
  reset_history?: boolean;
}

export interface JobBuilderLinkResponse {
  job_builder_id: number;
  job_id: number;
  builder_id: number;
  company_name: string;
  role: JobBuilderRole | string;
  chat_from: number;
  is_active: boolean;
  /** Attach only: the firm was already live on the job (nothing changed). */
  already_attached?: boolean;
  /** Attach only: the D7 notice was posted into the thread. */
  notice_posted?: boolean;
}

export interface JobBuilderDetachResponse {
  job_id: number;
  builder_id: number;
  detached: boolean;
  already_detached: boolean;
  notice_posted: boolean;
}

/** One selectable builder login from GET staff/chat/import/participants. */
export interface ChatImportBuilderOption {
  portal_user_id: number;
  name: string;
  firm: string;
  email: string;
  is_active: boolean;
}

/** An attached firm with no login yet — offered as a "create builder access"
 *  mapping (POST builders/{id}/invite with send_invite_email=false). */
export interface ChatImportCreatableBuilder {
  builder_id: number;
  firm: string;
  contact_name: string;
  email: string;
  has_email: boolean;
}
